/**
 * BillToPanel — R4 (v0.14) — Variant A "document-style" with Jason's amendments
 *
 * The expanded BILL TO panel on InvoiceDetailPage. Replaces the v0.12-era
 * three-dot-menu / hover-pencil card with one inline-editable row per BILL TO
 * field. Per the R4 mockup spec (Variant A + Jason's amendments):
 *
 *   1. Always-visible pencils (NOT hover-only). Slate-400 → slate-700 on hover.
 *   2. Source labels promoted from `text-xs text-slate-500` to
 *      `text-sm text-slate-600` so a 50-year-old user reads them without
 *      leaning in.
 *   3. Single "Recipients" list. Every row says TO or CC (SPEC-christa-contacts-identity
 *      R1 AC16), in the same role and order as the "Send this invoice to" card on the
 *      same page, so the two panels can never look like they disagree. TO is the person
 *      the invoice goes to; CC gets a copy.
 *   4. Per-recipient star toggle. ★ = saved as accounting default for the
 *      invoice's DBA; ☆ = ad-hoc on this invoice only. Click fires
 *      PropagationModal with field='star_toggle'.
 *   5. No payment_terms / currency in this panel.
 *   6. No BCC / internal-copies section.
 *
 * Identity edits use the shared reviewed preview/apply flow. The old
 * propagation modal still names address destination choices, then routes each
 * choice to a typed reviewed intent. Recipients remain on their own workflow.
 *   - RECIPIENTS   → POST/DELETE /api/invoices/{id}/contacts with
 *                    field_update_mode, ★ → POST .../toggle-default
 *   - PROJECT      → READ-ONLY (Christa Issue 11 sanity cue)
 *
 * Propagation modal is R3b's deliverable; this component imports a stub from
 * ./PropagationModal until R3b lands. Stub auto-confirms with defaultMode so
 * the full round-trip works end-to-end in tests.
 *
 * Risks handled:
 *   - save_to_project clears billing_address_source='custom' (by design).
 *   - save_dba_default with no establishment_id → R3a returns applied=false,
 *     reason. We pre-disable that mode in availableModes when invoice has no
 *     establishment so the user never picks an unworkable option.
 *   - recipients_meta source='user_added' for dangling FK (deleted org_contact):
 *     render "(removed contact)" and omit the star toggle.
 */
import { useState, useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Pencil,
  Trash2,
  Star,
  Plus,
  AlertTriangle,
  MailX,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useUpdateInvoiceInline,
  useAddInvoiceContact,
  useRemoveInvoiceContact,
  useToggleRecipientDefault,
  usePromoteInvoiceContact,
  useUpdateInvoiceContact,
  useEligibleInvoiceContacts,
  useCascadeWrite,
  useCascadeRetroactive,
  useAddDbaInContext,
} from '@/hooks';
import {
  PropagationModal,
  type FieldUpdateMode,
  type PropagationField,
  type PropagationContext,
  type PropagationConfirmExtras,
  type RecipientAddInfo,
} from './PropagationModal';
import { RoleBadge } from './RoleBadge';
import {
  RecipientRoleSelect, RoleChangeDialog, ROLE_LOCKED_STATUSES, type Role, type RoleChangeRequest,
} from './RecipientRoleChange';
import { isSent, type SendEvidence } from '@/lib/sendEvidence';
// R4 (invoice-identity-cascade) — the "Christa Land" inline cascade surface.
// These replace the modal-driven DBA flow with an inline picker (no modal, no
// kebab), resolution-aware source tags, an easy add-DBA affordance, and the
// confirm-gated legal-name blast radius.
import {
  InlineCascadePicker,
  type CascadeLevel,
  type PickerEditReason,
} from './InlineCascadePicker';
import { AddDbaInline } from './AddDbaInline';
import { buildSourceTag } from './billToSourceTag';
import { computeGate, type IdentityResolutionDto } from './billToGate';
// Re-export so callers (InvoiceDetailPage) get the resolution DTO type from the
// same surface they import BillToPanel from.
export type { IdentityResolutionDto } from './billToGate';
import { buildSaveResult, type SaveResult } from './billToConfirmation';
// Wave 3 (v0.14 org-change): CLIENT row at the top of the panel + stepper.
// The stepper is a sibling component to PropagationModal — not a replacement
// for it. R3 propagation behavior (DBA / address / legal_name / recipient
// edits) is unchanged.
import { OrgChangeStepper, type OrgChangeRecipientPreview } from './OrgChangeStepper';
import { InvoiceChangeReview } from './InvoiceChangeReview';
import { ProjectPayerChoice } from './ProjectPayerChoice';
import type { InvoiceChangeIntent, InvoiceDocumentHistoryData,
  InvoiceIdentityFieldName } from '@/lib/invoiceSelectiveHistoryContracts';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { api } from '@/lib/api';
import type { EligibleContact } from '@/lib/schemas';
import type { InvoiceEffectiveIdentity } from '@/lib/schemas';

// ---------- types ----------

export interface BillToInvoice {
  id: string;
  /** Zero-padded invoice number (her native language) — drives the binary
   * persistence-choice consequence copy ("Fix only invoice 0602"). */
  invoice_number?: string | null;
  organization_id?: string | null;
  project_id?: string | null;
  /** "A-197": with the name, how the project is named in the add-recipient modal. */
  project_number?: string | null;
  project_name?: string | null;
  project_address?: string | null;
  client_scope_id?: string | null;
  client_name?: string | null;
  client_legal_name?: string | null;
  identity_effective?: InvoiceEffectiveIdentity;
  establishment_id?: number | null;
  establishment_name?: string | null;
  establishment_legal_name?: string | null;
  establishment_address?: string | null;
  hide_dba?: boolean | null;
  billing_address_source?: 'organization' | 'project' | 'custom' | 'establishment' | null;
  billing_address_override?: string | null;
  frozen_billing_address?: string | null;
  org_billing_address?: string | null;
  org_address?: string | null;
  /** Invoice status. R3 (invoice-identity-correctable): the freeze is DROPPED —
   * BILL TO is correctable on ANY status incl. SENT. Status no longer gates
   * editability; it survives on the type for display captions only. */
  status?: 'draft' | 'ready' | 'sent' | 'paid' | 'partially_paid' | 'overdue' | 'cancelled' | null;
  /** The one send rule decided on the server. A Draft or Ready invoice with a recorded send locks the role choice too. */
  send_evidence?: SendEvidence | null;
}

/**
 * R3 (invoice-identity-correctable) — G3 / AC4 / AC5: sent invoices are FULLY
 * CORRECTABLE.
 *
 * The freeze-at-send from PR #192 (Hotfix2 Bug #3's `draft`/`ready`-only lock)
 * is being DROPPED. It made a SENT invoice immutable + un-correctable — the
 * opposite of Christa's goal (she needs to fix a genuine mistake on a sent
 * invoice HERSELF, on the invoice in front of her, without a "return to draft
 * first" dead-end). Render LIVE; the only stored identity is an explicit
 * per-invoice OVERRIDE. Every identity one-off control + recipient add/remove
 * is available on ANY status. So editability is unconditional.
 */
export function isBillToEditable(_status: BillToInvoice['status']): boolean {
  return true;
}

/**
 * R2 AC12 (production finding r): the address shown on the Bill To panel and the caption that names where it comes
 * from, decided together so they cannot disagree. Before, the caption named the chosen SOURCE ("DBA billing address")
 * even when that source had nothing to show and the value had quietly fallen back to the project address, while the
 * DBA row said "(no DBA selected)".
 *
 *   frozen invoice address          -> "Invoice-specific billing address"
 *   custom                          -> "Custom address, this invoice only"
 *   DBA source, DBA attached with its own address -> "DBA billing address"
 *   DBA source otherwise (no DBA, or the DBA has no address) -> the project address, "Project address"
 *   project                         -> "Project address"
 *   client source with a billing address -> "Client billing address"
 *   client source without one       -> the client's physical address, "Client address"
 */
export function resolveBillToAddress(invoice: Pick<BillToInvoice,
  'frozen_billing_address' | 'billing_address_source' | 'billing_address_override' | 'establishment_id'
  | 'establishment_address' | 'project_address' | 'org_billing_address' | 'org_address'>): { value: string; label: string } {
  if (invoice.frozen_billing_address !== null && invoice.frozen_billing_address !== undefined) {
    return { value: invoice.frozen_billing_address, label: 'Invoice-specific billing address' };
  }
  const source = invoice.billing_address_source || 'organization';
  if (source === 'custom') {
    return { value: invoice.billing_address_override || '', label: 'Custom address, this invoice only' };
  }
  if (source === 'establishment') {
    if (invoice.establishment_id && invoice.establishment_address) {
      return { value: invoice.establishment_address, label: 'DBA billing address' };
    }
    return { value: invoice.project_address || '', label: 'Project address' };
  }
  if (source === 'project') {
    return { value: invoice.project_address || '', label: 'Project address' };
  }
  if (invoice.org_billing_address) return { value: invoice.org_billing_address, label: 'Client billing address' };
  if (invoice.org_address) return { value: invoice.org_address, label: 'Client address' };
  return { value: '', label: 'Client billing address' };
}

export interface ScopeState {
  scope_id: string | null;
  scope_establishment_id: number | null;
  scope_establishment_name: string | null;
  is_invoiced: boolean;
  sibling_scope_count: number;
  scope_invoice_count_total: number;
  scope_invoice_count_sent: number;
}

export interface AddressState {
  source: string;
  project_address_set: boolean;
  establishment_address_set: boolean;
  siblings_using_project_source: number;
  siblings_using_establishment_source: number;
}

export interface LegalNameState {
  source: string;
  establishment_legal_name_set: boolean;
  siblings_using_this_establishment: number;
}

export interface RecipientMeta {
  invoice_contact_id: string | null;
  contact_id: string | null;
  name: string | null;
  email: string | null;
  role: string;
  source: 'snapshot_dba' | 'snapshot_org' | 'user_added' | 'typed_in';
  is_dba_accounting: boolean;
  is_org_accounting: boolean;
  /** R2 AC13: one of the project's saved people (so a role change can also change the project's role). */
  is_project_default?: boolean;
  /** M2: the invoice's project has people saved of its own (its set always wins over the client's defaults). */
  project_has_own_people?: boolean;
}

export interface BillToPanelProps {
  invoice: BillToInvoice;
  establishments: Array<{ id: number; name: string; is_default: boolean }>;
  recipientsMeta: RecipientMeta[];
  scopeState: ScopeState | null;
  addressState: AddressState | null;
  legalNameState: LegalNameState | null;
  // bill-to fix 2026-05: kept so the empty-TO fallback warning survives R4.
  toContactsMeta?: {
    explicit_count: number;
    fallback_active: boolean;
    fallback_contact: { contact_name: string; contact_email: string } | null;
    suppress_attention_line?: boolean;
  };
  onSendPreview?: () => void;

  // ---- R4 (invoice-identity-cascade) — Christa Land props ----
  //
  // R2's resolution bundle for THIS invoice (per-field source/suggested/
  // resolved markers + the loop-closure signal). Optional so existing callers
  // keep rendering; when present it drives the source tags + the gate. The
  // GET-invoice read path that surfaces this is wired by R5 / the operator on
  // the preview env (R2 shipped the engine + R4 consumes it); the panel reads
  // it via this prop so it's deterministically testable here.
  resolution?: IdentityResolutionDto | null;
  fieldProvenance?: InvoiceDocumentHistoryData['current_field_provenance'];
  /** Sibling-DRAFT count on this project (the count the project option shows). */
  projectDraftCount?: number;
  /** Total invoice count for the client across all projects (client option). */
  clientInvoiceCount?: number;
  /** True when this project genuinely bills more than one client (AC2 split). */
  multiClientProject?: boolean;
  /** The zero-padded invoice number a pulled value seeds from (provenance). */
  lastSentNumber?: string | null;
  /**
   * Called whenever the unresolved gate state changes (AC6). The page wires
   * this to disable PDF download + mark-as-sent. The REACT disable is the
   * visible layer over R1's SERVER gate at those entry points.
   */
  onGateStateChange?: (blocked: boolean) => void;
}

// ---------- helpers ----------

const FIELD_LABEL_CLS =
  'text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1';
const VALUE_CLS = 'text-base font-semibold text-slate-900';
// Jason amendment #2: source labels promoted from xs/slate-500 to sm/slate-600
// so a non-technical user can read them without leaning in.
const SOURCE_CLS = 'text-sm text-slate-600 mt-0.5';
const PENCIL_BTN_CLS =
  // Jason amendment #1: always-visible pencils. Color shift on hover instead of
  // opacity 0→100 — Christa explicitly complains about hidden controls.
  'p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors flex-shrink-0';

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <p className={FIELD_LABEL_CLS}>{children}</p>;
}

// ---------- component ----------

/**
 * The pinned contacts, named. Falls back to a count when the backend did not
 * send names (older build) so the line still says something true rather than
 * rendering an empty span.
 */
function formatPinnedNames(pin: { count: number; names?: string[] }): string {
  const names = (pin.names ?? []).filter(Boolean);
  if (names.length === 0) {
    return pin.count === 1 ? '1 saved contact' : `${pin.count} saved contacts`;
  }
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names[0]}, ${names[1]} and ${names.length - 2} other${names.length - 2 === 1 ? '' : 's'}`;
}

export function BillToPanel({
  invoice,
  establishments,
  recipientsMeta,
  scopeState,
  addressState,
  legalNameState,
  toContactsMeta,
  onSendPreview,
  resolution,
  fieldProvenance,
  projectDraftCount = 0,
  clientInvoiceCount = 0,
  multiClientProject = false,
  lastSentNumber,
  onGateStateChange,
}: BillToPanelProps) {
  const queryClient = useQueryClient();
  const updateInvoiceInline = useUpdateInvoiceInline();
  const addContact = useAddInvoiceContact();
  const removeContact = useRemoveInvoiceContact();
  const toggleDefault = useToggleRecipientDefault();
  const promoteContact = usePromoteInvoiceContact();
  const updateContact = useUpdateInvoiceContact();
  const { data: eligible } = useEligibleInvoiceContacts(invoice.id);
  // R4 — inline cascade write hooks (replace the modal-driven DBA flow).
  const cascadeWrite = useCascadeWrite();
  // B2: the retroactive apply is intentionally NOT auto-fired from the
  // going-forward path anymore (siblings keep their own identities). The hook +
  // endpoint stay wired so a future EXPLICIT opt-in can reuse them.
  const cascadeRetroactive = useCascadeRetroactive();
  void cascadeRetroactive;
  const addDbaInContext = useAddDbaInContext();

  // R8c follow-up (v0.14): two-tab race recovery. When a cascading PUT (any
  // field_update_mode that writes upstream of the invoice — legal_name
  // save_to_establishment, address save_to_establishment) returns 412
  // establishment_mismatch, close the modal, invalidate the invoice query so
  // the next render reflects the actual state, and toast the user.
  //
  // The body of the error is an HTTPError thrown by fetchAPI; its `message`
  // contains the JSON-serialized detail. We probe for the code rather than
  // the status (different errors may surface differently across hook layers).
  // Source: R8c-final-closeout-2026-05-27.md Phase 3 finding #1.
  const handlePotentialMismatch = (err: any): boolean => {
    const msg = String(err?.message ?? err ?? '');
    const isMismatch =
      err?.status === 412 ||
      msg.includes('establishment_mismatch') ||
      msg.includes('"code":"establishment_mismatch"');
    if (!isMismatch) return false;
    setPending(null);
    setEditingField(null);
    queryClient.invalidateQueries({ queryKey: ['invoices', invoice.id] });
    queryClient.invalidateQueries({ queryKey: ['invoices', invoice.id, 'full'] });
    toast.warning(
      'This invoice was just modified. The page has been refreshed, so please try again.',
    );
    return true;
  };

  // Recipients-query invalidation (live re-render fix, 2026-06).
  //
  // The recipients list + the amber "Send without TO recipient" empty-state
  // warning both render off recipients_meta, which lives inside the
  // ['invoices', id, 'full'] payload the page reads. After a recipient add we
  // must invalidate that query so the list + warning re-render WITHOUT a manual
  // reload. The useAddInvoiceContact hook already invalidates on success, but
  // the panel's "Add as one-off" (typed-in, field_update_mode='invoice_only')
  // branch skips the propagation modal AND — because tests mock the hook and
  // the live flow saw "added" against an unchanged list — wasn't guaranteed to
  // pick up the refresh. Mirror the hook's _invalidateInvoiceCaches here at the
  // call site so the re-render is owned by the panel, not just the hook.
  // Mirrors REACT_QUERY_PATTERNS.md: shortest prefix that covers the variant,
  // list queries via predicate, no detail-id specific invalidations.
  const invalidateRecipients = () => {
    // The detail payload carrying recipients_meta (+ the empty-state warning).
    queryClient.invalidateQueries({ queryKey: ['invoices', invoice.id, 'full'] });
    // List rows carry the recipient column too — invalidate non-UUID,
    // non-sentinel ['invoices', ...] keys (mirrors _invalidateInvoiceCaches /
    // useRefreshInvoiceContacts in useInvoices.ts).
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        if (key[0] !== 'invoices' || key.length < 2) return false;
        const second = key[1];
        if (typeof second === 'string' && second.includes('-')) return false;
        if (second === 'next-number') return false;
        return true;
      },
    });
  };

  // Hotfix2 — Bug #3: status gating. Drives whether pencils, add/remove
  // affordances, star toggles, and the propagation modal are rendered.
  // See isBillToEditable() above.
  const isEditable = isBillToEditable(invoice.status);

  // ---- inline edit state (one row at a time) ----
  const [editingField, setEditingField] = useState<
    null | 'legal_name' | 'dba' | 'address' | 'recipient_add'
  >(null);
  // free-text drafts
  const [addressMode, setAddressMode] = useState<
    'project' | 'establishment' | 'organization' | 'custom'
  >('project');
  const [addressDraft, setAddressDraft] = useState('');
  // Polish wave 2 — B5 (2026-05-30): defensive refs that mirror addressMode
  // and addressDraft. The staging sweep observed a "first Apply on custom
  // address sometimes silently no-ops; retry works" pattern (sweep Risks #5).
  // Investigation:
  //   - applyAddress() short-circuits with a no-op when source equals
  //     invoice.billing_address_source AND customValue equals override
  //     (lines ~574-580). If the click handler reads stale state where mode
  //     hadn't switched to 'custom' yet, source='project' matches
  //     invoice='project' → silent no-op.
  //   - React 18 batches state updates within event handlers; the radio
  //     click and Save click are separate events, so in theory the Save
  //     handler sees the post-radio state via the most recent closure.
  //   - Could not reproduce in unit tests with userEvent.setup().
  //   - But the sweep saw it on staging; the cost of a defensive ref read
  //     is one extra useEffect + ref + DOM-style read at Save time, and
  //     it eliminates any possible stale-closure cause.
  // Belt-and-braces: refs always carry the latest values; Save reads the
  // refs instead of closing over state. The textarea also writes its own
  // value through a ref so a missed onChange flush can't strand the input.
  const addressModeRef = useRef<typeof addressMode>(addressMode);
  const addressDraftRef = useRef<string>(addressDraft);
  useEffect(() => {
    addressModeRef.current = addressMode;
  }, [addressMode]);
  useEffect(() => {
    addressDraftRef.current = addressDraft;
  }, [addressDraft]);
  // Polish wave 3 — W3-3 (2026-05-30): synchronous Apply re-entry guard
  // for the address Save → Apply flow (mirrors W3-2 on PropagationModal,
  // which is the B2 pattern from OrgChangeStepper). Staging re-sweep
  // observed that triple-clicking the address Save button used to fire
  // the address-Save handler 3× before the propagation modal opened and
  // its own race-guard could take over. For non-custom flips (which
  // bypass the modal entirely and PUT directly via updateInvoiceInline)
  // this would have fired 3 PUTs to /api/invoices/:id; for custom flips
  // it would have setPending three times, opening / re-opening the
  // modal in rapid succession. Either way, gate the Save click
  // synchronously. Released when the modal closes (closePending) or
  // when the inline mutate completes; reset whenever the address
  // editor opens so reopening after a cancel doesn't perma-block Save.
  const addressSaveInFlightRef = useRef<boolean>(false);
  // dba inline
  const [dbaDraft, setDbaDraft] = useState<number | null>(invoice.establishment_id ?? null);
  const [dbaHideOnPdf, setDbaHideOnPdf] = useState<boolean>(invoice.hide_dba ?? false);

  // ---- R4 (Christa Land) inline-cascade state ----
  //
  // The DBA row now drives the InlineCascadePicker instead of PropagationModal.
  // `dbaEditMode`: 'closed' | 'picker' | 'add_dba'. `saveResult` carries the
  // EVIDENTIARY confirmation (or inline error) so a Save is always visibly
  // terminal (AC8) — no silent no-op.
  const [dbaEditMode, setDbaEditMode] = useState<'closed' | 'picker' | 'add_dba'>('closed');
  const [saveResult, setSaveResult] = useState<SaveResult | null>(null);
  const [reviewIntent, setReviewIntent] = useState<InvoiceChangeIntent | null>(null);
  const [projectPayerOpen, setProjectPayerOpen] = useState(false);
  // R2 AC13: the TO/CC choice on a recipient row, and the question it asks.
  const [roleChange, setRoleChange] = useState<(RoleChangeRequest & { rowId: string }) | null>(null);
  const [roleRefusal, setRoleRefusal] = useState<string | null>(null);
  const [reviewProjectIds, setReviewProjectIds] = useState<string[]>([]);
  // The instant the picker opened — bounds the retroactive apply set (AC6/R3).
  const pickerOpenedAtRef = useRef<string | null>(null);
  // recipient add popover
  const [oneOffName, setOneOffName] = useState('');
  const [oneOffEmail, setOneOffEmail] = useState('');

  // ---- Wave 3 (v0.14 org-change) stepper state ----
  //
  // CLIENT row's pencil opens the OrgChangeStepper. Independent of the
  // PropagationModal — they're orthogonal flows (this changes
  // invoice.organization_id; PropagationModal handles per-field cascades
  // within the current organization).
  //
  // §5.13 carry-through: when the user picks a DBA in the stepper and
  // chooses "Bill under parent," we want the new invoice's DBA pre-set
  // to the DBA she searched on. As of the R8e/R8f fixwave (2026-05-28),
  // the stepper includes `establishment_id` directly in the cascade PUT
  // body and the backend cascade honors it inside the single transaction
  // (no separate follow-up mutation, no race window, no toast spam).
  // The `onCarryThroughDBA` callback is no longer needed but kept on
  // OrgChangeStepperProps for backwards compatibility / future use.
  const [orgChangeOpen, setOrgChangeOpen] = useState(false);

  // ---- pending PropagationModal state (one at a time) ----
  type Pending = {
    field: PropagationField;
    availableModes: FieldUpdateMode[];
    defaultMode: FieldUpdateMode;
    context: PropagationContext;
    oldValue?: string | null;
    newValue?: string | null;
    starToggleAction?: 'add_as_dba_default' | 'remove_from_dba_default';
    /** Star only: the project's label when it has people saved of its own (they win over client defaults). */
    starProjectLabel?: string;
    starProjectSaveState?: 'available' | 'invoice_locked' | 'already_saved' | 'missing_email' | 'unavailable';
    /** Per-mode consequence line, when the caller knows something the modal's
     *  static copy table cannot (e.g. this is the LAST recipient). */
    modeNotes?: Partial<Record<FieldUpdateMode, string>>;
    /** Recipient add only: the role control and the people the project option will save. */
    recipientAdd?: RecipientAddInfo;
    apply: (mode: FieldUpdateMode, extras?: PropagationConfirmExtras) => void | Promise<void>;
  };
  const [pending, setPending] = useState<Pending | null>(null);

  const closePending = () => {
    setPending(null);
    // W3-3: release the address Save race-guard when the propagation
    // modal closes (success or cancel). For the custom-address branch,
    // the modal close is the terminal signal that the Save click flow
    // has finished — re-allow Save on next reopen of the editor.
    addressSaveInFlightRef.current = false;
  };

  // ---------- Wave 3 (org-change) derived values ----------

  // §0.2 contact-scope tags for the Step 3 cascade preview. Each
  // recipientsMeta row maps to a human-readable scope.
  const orgChangeRecipientsPreview: OrgChangeRecipientPreview[] = useMemo(() => {
    return recipientsMeta
      .filter((r) => r.invoice_contact_id) // only real rows
      .map((r) => {
        let scopeTag: string | null = null;
        if (r.source === 'snapshot_dba' && invoice.establishment_name) {
          scopeTag = `For ${invoice.establishment_name} (DBA-scoped)`;
        } else if (r.source === 'snapshot_org' && invoice.client_name) {
          scopeTag = `For the whole client (${invoice.client_name}), affects all DBAs`;
        } else if (r.source === 'typed_in') {
          scopeTag = 'Added just on this invoice';
        } else if (r.source === 'user_added') {
          scopeTag = 'Added just on this invoice';
        }
        return {
          name: r.name,
          email: r.email,
          scopeTag,
        };
      });
  }, [recipientsMeta, invoice.establishment_name, invoice.client_name]);

  // CLIENT row label + source caption per §0.2 vocabulary lock.
  const projectPin = resolution?.project_recipient_pin ?? null;
  // True only when an empty recipient list really DOES fall back to a named
  // person: the fallback is active, not suppressed, and a contact exists.
  const primaryFallbackActive =
    !!toContactsMeta?.fallback_active &&
    !toContactsMeta?.suppress_attention_line &&
    !!toContactsMeta?.fallback_contact;
  const clientValue = invoice.client_name || null;
  const clientSourceCaption =
    invoice.client_legal_name && invoice.client_legal_name !== invoice.client_name
      ? `client of record (legal name ${invoice.client_legal_name})`
      : 'client of record';

  // R3 (invoice-identity-correctable): the §4.8 "return it to Draft first"
  // locked-tooltip is GONE. The freeze is dropped — the client is correctable
  // on any status, so there is no dead-end copy to render. (AC5: no "return to
  // draft first" dead-end.)

  // ---------- derived display values ----------

  // The GET projection shares the PDF resolver. An explicit blank is a value,
  // so only an absent projection uses the older-server compatibility fallback.
  const effectiveLegalName = invoice.identity_effective?.legal_name;
  const currentLegalName = invoice.identity_effective?.current_legal_name;
  const legalNameValue = effectiveLegalName?.value ?? (
    invoice.client_legal_name || invoice.establishment_name || invoice.client_name || ''
  );
  const legalNameIsKept = effectiveLegalName?.source === 'invoice_override';
  const legalNameSource = useMemo(() => {
    if (legalNameIsKept) {
      if (currentLegalName && currentLegalName.value !== legalNameValue) {
        const sourceLabel = currentLegalName.source === 'establishment'
          ? 'DBA legal name'
          : currentLegalName.source === 'organization_name_fallback'
            ? 'client display name' : 'client legal name';
        return `Kept for this invoice. Current ${sourceLabel}: ${currentLegalName.value || 'not set'}.`;
      }
      return 'Kept for this invoice.';
    }
    // UX1 (v0.14 polish): §0.2 vocab lock — surface "DBA" not "establishment"
    // in user-facing source captions on the panel Christa lives in.
    if (legalNameState?.source === 'establishment' && invoice.establishment_name) {
      return `DBA legal name · ${invoice.establishment_name}`;
    }
    if (legalNameState?.source === 'organization') {
      return 'client legal name';
    }
    // No backend hint — best-effort guess.
    return invoice.establishment_name
      ? `DBA legal name · ${invoice.establishment_name}`
      : 'client legal name';
  }, [legalNameState, invoice.establishment_name, legalNameIsKept, currentLegalName, legalNameValue]);

  // DBA row. The PDF prints a DBA line whenever the trading name differs from
  // the legal/company name — and that happens even with NO establishment, when
  // an org's `name` differs from its `legal_name` (the org name prints as the
  // DBA). hide_dba suppresses that line, so the panel must surface the printed
  // DBA and keep the hide toggle reachable in BOTH cases. Mirrors
  // pdf_generator.py + templates/invoice.html exactly:
  //   company = COALESCE(est.legal_name, o.legal_name, o.name)
  //   dba     = COALESCE(est.name, o.name)   → row prints when dba != company
  // Establishment fields only count when an establishment is actually attached
  // (the backend JOINs on establishment_id, so est.* is NULL otherwise). Mirror
  // that here so a stale establishment_name/legal_name with no establishment_id
  // can't fabricate a phantom DBA.
  const estAttached = !!invoice.establishment_id;
  const pdfCompanyName = effectiveLegalName?.value ?? (
    (estAttached ? invoice.establishment_legal_name : null) ||
    invoice.client_legal_name || invoice.client_name || ''
  );
  const pdfDbaName =
    (estAttached ? invoice.establishment_name : null) || invoice.client_name || '';
  const dbaPrintsOnPdf = !!pdfDbaName && pdfDbaName !== pdfCompanyName;

  const dbaValue =
    invoice.establishment_name ||
    (dbaPrintsOnPdf ? invoice.client_name : null) ||
    '(no DBA selected)';
  // UX1 (v0.14 polish): §0.2 vocab lock — "DBA name" not "establishment name".
  const dbaSource = invoice.establishment_id
    ? 'DBA name'
    : dbaPrintsOnPdf
      ? 'client trading name'
      : 'client default (no DBA)';

  // ADDRESS row: the value and its caption come from ONE helper (R2 AC12), so they cannot disagree.
  const resolvedAddress = useMemo(() => resolveBillToAddress(invoice), [invoice]);
  const addressValue = resolvedAddress.value;
  const addressSourceLabel = resolvedAddress.label;

  // The reset target is the existing invoice's source, not the independent
  // create-time default. Ignore any frozen invoice address when naming it.
  const currentSourceAddress = (() => {
    const source = invoice.billing_address_source || 'organization';
    if (source === 'custom') return { label: 'this invoice’s custom address', value: invoice.billing_address_override ?? '' };
    if (source === 'establishment') return invoice.establishment_address
      ? { label: 'DBA billing address', value: invoice.establishment_address }
      : { label: 'project billing address (DBA address unavailable)', value: invoice.project_address ?? '' };
    if (source === 'project') return { label: 'project billing address', value: invoice.project_address ?? '' };
    if (invoice.org_billing_address) return { label: 'client billing address', value: invoice.org_billing_address };
    return { label: 'client physical address', value: invoice.org_address || '' };
  })();

  // Polish wave 2 — B3 (2026-05-30): port the OrgChangeConfirm slate-100 chip
  // surface to the BillToPanel's address source caption so Christa gets the
  // same visual signal that "this is the address source" on the panel itself.
  // Always slate — the panel is a static render of current state, not a
  // changeKind preview. The chip on OrgChangeConfirm varies (slate/amber/blue)
  // based on whether the source flips on org-change; on the panel itself
  // there's no upcoming change to signal, so chip stays slate and acts as a
  // consistent affordance pattern across the BILL TO surface.
  const ADDRESS_SOURCE_CHIP_CLS =
    'inline-flex items-center text-xs font-medium text-slate-700 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded';

  // ---------- Christa-friction-polish CRIT-4 / 2c: caption suppression ----------
  //
  // Goal: drop visual density from ~18 elements to ~11 in the dominant
  // 87% NULL-DBA / zero-recipients case. CLIENT caption always shows (the
  // anchor); other row captions render only when they carry override-relevant
  // signal (value differs from inherited default, source is an explicit
  // non-default choice, etc).
  //
  //   Invoice For: show when value differs from client name OR sourced from
  //                establishment legal name. Hides when the value equals the
  //                client name with no DBA legal-name override.
  //   DBA       : show only when DBA is set (suppresses "client default — no
  //                DBA" caption — the value "(no DBA selected)" already says it).
  //   Address   : show when source ≠ default. Default = "project" or null.
  //                Show for custom / establishment / explicit organization.
  //   Recipients: keep all captions (intentional context per spec).
  const showLegalNameSource =
    legalNameIsKept ||
    legalNameValue !== invoice.client_name ||
    legalNameState?.source === 'establishment';
  const showDbaSource = !!invoice.establishment_id || dbaPrintsOnPdf;
  const showAddressSource =
    invoice.billing_address_source === 'custom' ||
    invoice.billing_address_source === 'establishment' ||
    invoice.billing_address_source === 'organization' ||
    (invoice.frozen_billing_address !== null && invoice.frozen_billing_address !== undefined);

  // ---------- R4 (Christa Land): gate + source tags + cascade save ----------

  // The unresolved gate (AC6 / AC10a). `hasClient` is the decisive input — an
  // invoice with no client is the genuinely-blocking `needs a client` state.
  // The REACT disable here is the VISIBLE layer over R1's SERVER gate.
  const hasClient = !!invoice.organization_id;
  const gate = useMemo(
    () => computeGate({ hasClient, resolution }),
    [hasClient, resolution],
  );
  // Surface the gate to the page so PDF download + mark-as-sent disable (AC6).
  useEffect(() => {
    onGateStateChange?.(gate.blocked);
  }, [gate.blocked, onGateStateChange]);

  // Per-row source tags from R2 resolution (AC1/AC5/AC9). `needsClient` makes
  // the unresolved DBA the blocking ⚠ state ONLY when there's no client; with a
  // client an unresolved DBA is a quiet fillable blank, not a warning.
  const dbaSourceTag = useMemo(
    () =>
      buildSourceTag(resolution?.establishment, {
        clientName: invoice.client_name,
        lastSentNumber,
        needsClient: !hasClient,
      }),
    [resolution?.establishment, invoice.client_name, lastSentNumber, hasClient],
  );

  // AC3 — why the DBA edit is being opened, drives the picker default.
  const dbaEditReason: PickerEditReason = useMemo(() => {
    const est = resolution?.establishment;
    if (!est || !est.resolved) return 'unresolved';
    if (est.suggested) return 'pulled_unconfirmed'; // Tier-2 awaiting a yes
    return 'overriding'; // an already-resolved value is being changed
  }, [resolution?.establishment]);

  // DBA changes and deliberate going-forward defaults use one reviewed plan.
  // That includes a future default clear when this invoice already has no DBA;
  // comparing only the current invoice would miss that requested action.
  // PDF visibility is a separate reviewed choice. The legacy fallback below
  // remains for invoice shapes without an organization, but ordinary linked
  // invoices must never compose two blind cascade writes here.
  const saveDbaCascade = (level: CascadeLevel, newEstId: number | null) => {
    const dbaChanged = newEstId !== (invoice.establishment_id ?? null);
    const hideChangedForReview = dbaHideOnPdf !== (invoice.hide_dba ?? false);
    if (dbaChanged && hideChangedForReview) {
      setSaveResult({ kind: 'error', message: 'Review the DBA selection and the PDF visibility setting separately so each impact is clear.' });
      return;
    }
    if ((dbaChanged || level !== 'this_invoice') && !hideChangedForReview && invoice.organization_id) {
      if (level === 'this_invoice') {
        setReviewIntent({ intent: 'invoice_assignment', invoice_id: invoice.id,
          organization_id: invoice.organization_id, target_organization_id: invoice.organization_id,
          target_establishment_id: newEstId });
      } else if (level === 'this_project' && invoice.project_id && invoice.client_scope_id) {
        setReviewIntent({ intent: 'identity_default', organization_id: invoice.organization_id,
          default_level: 'this_project', default_field: 'dba', future_defaults: true,
          focused_invoice_id: invoice.id,
          project_ids: [invoice.project_id], client_scope_ids: [invoice.client_scope_id],
          default_establishment_id: newEstId });
      } else if (level === 'this_client') {
        setReviewIntent({ intent: 'identity_default', organization_id: invoice.organization_id,
          default_level: 'this_client', default_field: 'dba', future_defaults: true,
          focused_invoice_id: invoice.id,
          default_establishment_id: newEstId });
      } else {
        setSaveResult({ kind: 'error', message: 'This project has no client billing scope. Review this invoice separately.' });
        return;
      }
      setDbaEditMode('closed');
      return;
    }
    if (hideChangedForReview && level === 'this_invoice') {
      setReviewIntent({ intent: 'invoice_options', invoice_id: invoice.id, invoice_hide_dba: dbaHideOnPdf });
      setDbaEditMode('closed');
      return;
    }
    if (hideChangedForReview && level !== 'this_invoice' && invoice.organization_id) {
      if (level === 'this_project' && invoice.project_id && invoice.client_scope_id) {
        setReviewIntent({ intent: 'identity_default', organization_id: invoice.organization_id,
          default_level: 'this_project', default_field: 'hide_dba', future_defaults: true,
          focused_invoice_id: invoice.id,
          project_ids: [invoice.project_id], client_scope_ids: [invoice.client_scope_id],
          default_hide_dba: dbaHideOnPdf });
      } else if (level === 'this_client') {
        setReviewIntent({ intent: 'identity_default', organization_id: invoice.organization_id,
          default_level: 'this_client', default_field: 'hide_dba', future_defaults: true,
          focused_invoice_id: invoice.id,
          default_hide_dba: dbaHideOnPdf });
      } else {
        setSaveResult({ kind: 'error', message: 'This project has no client billing scope. Review this invoice separately.' });
        return;
      }
      setDbaEditMode('closed');
      return;
    }
    const newEst = establishments.find((e) => e.id === newEstId);
    const savedValue = newEst?.name || invoice.client_name || 'this client';
    const changed = newEstId !== (invoice.establishment_id ?? null);

    // Hide-DBA is a per-invoice PDF setting the picker also exposes (Christa's
    // legal-name-only invoices, e.g. Living Balance / Powell St). Persist a flip
    // with the same Save — a plain hide_dba PUT on THIS invoice (no upstream
    // column to cascade). Works for establishment-less clients too. Fired
    // independently of the DBA change so toggling hide alone still saves.
    const hideChanged = dbaHideOnPdf !== (invoice.hide_dba ?? false);

    // Names the scope actually saved, for both the confirmation and the
    // partial-save error below. Declared out here so the async callbacks and
    // the synchronous this_invoice path cannot drift into describing the save
    // differently.
    const hideScope =
      level === 'this_project'
        ? ` and future ${invoice.project_name || 'project'} invoices`
        : level === 'this_client'
          ? ` and future ${invoice.client_name || 'client'} invoices`
          : '';
    const hideOnlySuccess = () => {
      setSaveResult({
        kind: 'success',
        message: dbaHideOnPdf
          ? `Saved. The DBA line is now hidden on this invoice${hideScope}.`
          : `Saved. The DBA line will show on this invoice${hideScope}.`,
        affectedNumbers: [],
      });
      setDbaEditMode('closed');
    };

    if (hideChanged) {
      // 2026-08-03: the optimistic write is the HOOK's job, not ours. This
      // block used to hand-write hide_dba into ['invoices', id] and
      // ['invoices', id, 'full'] right here, with no snapshot and no rollback,
      // on a fire-and-forget `mutate` with no per-call onError. Two defects
      // fell out of that:
      //   1. A rejected PUT left hide_dba stuck at the optimistic value — the
      //      PDF-hide toggle silently disagreed with the server.
      //   2. Worse, these writes ran BEFORE useUpdateInvoiceInline's own
      //      onMutate (which React Query invokes in a microtask, after this
      //      synchronous call returns), so the hook snapshotted the value we
      //      had ALREADY overwritten. Its rollback then "restored" the
      //      optimistic value — the mutation-level rollback was actively
      //      neutralised by the hand-write.
      // useUpdateInvoiceInline already writes both detail key formats AND every
      // list cache in onMutate, and rolls all three back in onError (v0.14.4
      // R5), so deleting the hand-writes is a strict superset of what was here
      // plus a working rollback.
      updateInvoiceInline.mutate({
        id: invoice.id,
        data: { hide_dba: dbaHideOnPdf },
      });
      // 2026-08-08: and CASCADE it when she picked a going-forward level.
      //
      // The comment above used to say hide_dba had "no upstream column to
      // cascade". That was false. V188 added project_scopes.default_hide_dba and
      // organizations.default_hide_dba and set_hide_dba_default to write them,
      // and then no UI ever called it and nothing READ them -- so unlike the
      // recipient defaults (written and ignored), this pair was never written at
      // ALL. Both halves land here. models/invoices.py now reads that chain
      // (project -> client -> establishment default -> false), which makes the
      // write meaningful for the first time.
      //
      // Until now the picker offered "all future {project} invoices", saved the
      // hide for exactly one invoice, and she re-ticked it every month on the
      // legal-name-only clients (Living Balance, Powell St -- inv 618).
      if (level !== 'this_invoice') {
        cascadeWrite.mutate(
          {
            id: invoice.id,
            data: { field: 'hide_dba', level, hide_dba: dbaHideOnPdf },
          },
          {
            // 2026-08-09: the confirmation used to be set SYNCHRONOUSLY, right
            // after this mutate() call, and this handler only fired a toast. So
            // a rejected cascade left a GREEN "Saved. ... and future Powell St
            // invoices." banner sitting over a write that never happened, with
            // a red toast beside it. That is the screen-disagrees-with-reality
            // class that made her start previewing every PDF before sending
            // ("I fear it didn't take in the system", 586, 2026-06-05), which
            // means we had reproduced it INSIDE the fix for it.
            //
            // The banner is now owned by these two callbacks. The DBA path
            // already worked this way via reportPartialSave; this path was the
            // inconsistent one.
            onSuccess: () => {
              if (!changed) hideOnlySuccess();
            },
            // The per-invoice PUT above is independent and may well have landed,
            // so this says what is true of each half rather than "nothing saved"
            // -- telling her nothing saved would send her back to redo an
            // invoice that is already correct.
            onError: (err: any) => {
              const reason = String(err?.message ?? '').trim();
              const message =
                `The DBA line is hidden on invoice ${invoice.invoice_number}, ` +
                `but it could not be saved for future invoices` +
                (reason ? `: ${reason}` : '.');
              // useCascadeWrite's own onError already toasts the server's
              // reason, so this sets the in-panel banner only -- two surfaces,
              // one story. On a combined save the DBA half owns the banner and
              // the toast is the whole message.
              if (changed) return;
              setSaveResult({ kind: 'error', message });
              setDbaEditMode('closed');
            },
          },
        );
      }
    }

    // A hide-only Save (no DBA change) is terminal on its own — no DBA cascade
    // write needed, and a this_invoice DBA write with the SAME establishment
    // would be a pointless no-op. Confirm + close.
    if (!changed && hideChanged) {
      // The message NAMES the scope that was actually saved. Confirming a
      // going-forward hide as "on this invoice PDF" is what kept her re-ticking
      // it every month on the legal-name-only clients (Living Balance / Powell
      // St, inv 618) -- the cascade is the whole point of the change and the
      // old message hid it.
      //
      // A going-forward level fired a cascade above, and that cascade's
      // callbacks own the banner: confirming here would be confirming a write
      // that has not resolved yet. Only the this_invoice level, which fires no
      // cascade, is genuinely terminal at this point.
      if (level === 'this_invoice') hideOnlySuccess();
      return;
    }

    const patchCurrentInvoiceCache = () => {
      // Patch the cache so the panel value reflects the SAVED state BEFORE the
      // confirmation renders (AC4 — no green-over-stale).
      const patch = {
        establishment_id: newEstId,
        establishment_name: newEst?.name ?? null,
      };
      queryClient.setQueryData(['invoices', invoice.id], (old: any) =>
        old ? { ...old, ...patch } : old,
      );
      queryClient.setQueryData(['invoices', invoice.id, 'full'], (old: any) =>
        old ? { ...old, invoice: { ...old.invoice, ...patch } } : old,
      );
    };

    const reportError = (err: any) => {
      if (handlePotentialMismatch(err)) return;
      setSaveResult(
        buildSaveResult({
          ok: false,
          changed,
          savedValue,
          level,
          projectName: invoice.project_name,
          clientName: invoice.client_name,
          affectedNumbers: [],
          errorMessage: String(err?.message ?? ''),
        }),
      );
    };

    // Step 2 failed AFTER step 1 already committed. The invoice in front of her
    // IS corrected; only the going-forward half did not save. The generic error
    // copy says "Nothing was changed", which on this path is false and is exactly
    // the screen-disagrees-with-reality problem that made her start previewing
    // every PDF before sending. Say what actually happened.
    const reportPartialSave = (err: any) => {
      if (handlePotentialMismatch(err)) return;
      const reason = String(err?.message ?? '').trim();
      setSaveResult({
        kind: 'error',
        message:
          `Invoice ${invoice.invoice_number} was changed to ${savedValue}, but it ` +
          `could not be saved for future invoices on this project` +
          (reason ? `: ${reason}` : '.') +
          ` This invoice is correct as it stands.`,
      });
      setDbaEditMode('closed');
    };

    // Step 2 (only for going-forward levels): set the project/client DEFAULT so
    // FUTURE invoices inherit it at create-time (the backend resolver applies
    // project default → client default → last-sent). Runs AFTER the current
    // invoice is already corrected in step 1. This write touches NO existing
    // invoice row, and we deliberately do NOT fire the retroactive apply: other
    // existing draft siblings keep their own identities (B2). The confirmation
    // therefore names NO affected siblings.
    const applyDefault = () => {
      // 2026-08-09: this used `level` directly, so a client level she reached
      // ONLY because the save carried a hide flip also set the client-wide DBA
      // default. InlineCascadePicker's own contract says that unlock "does NOT
      // relax the burial for a DBA/legal-entity change" -- the burial exists
      // because "it says override all for this client" (2026-05-28) is the
      // thing she objected to. The guarantee was documented and not implemented.
      //
      // On a genuinely multi-client project the client level is a real, buried,
      // deliberate choice for a DBA change, so it still stands there.
      const dbaLevel: CascadeLevel =
        level === 'this_client' && !multiClientProject ? 'this_project' : level;
      cascadeWrite.mutate(
        {
          id: invoice.id,
          data: { field: 'dba', level: dbaLevel, establishment_id: newEstId },
        },
        {
          // NOT reportError: step 1 has already committed, so "Nothing was
          // changed" would be a lie about the invoice in front of her.
          onError: reportPartialSave,
          onSuccess: () => {
            if (newEstId === null) {
              // This message hedged for one day (2026-08-07): it said the
              // default was cleared but future invoices would "follow the last
              // one you sent", because a NULL project default was
              // indistinguishable from "never set" and the resolver fell
              // through to the last ISSUED invoice -- which put the old DBA
              // straight back.
              //
              // V211's default_establishment_cleared is the tri-state that was
              // missing. _resolve_establishment now stops at a deliberate
              // clear, so the plain promise is true and gets said plainly.
              setSaveResult({
                kind: 'success',
                message:
                  `Saved. Invoice ${invoice.invoice_number} and future ` +
                  `invoices on this project bill under ` +
                  `${invoice.client_name || 'the client name'} with no separate ` +
                  `DBA line.`,
                affectedNumbers: [],
              });
              setDbaEditMode('closed');
              return;
            }
            setSaveResult(
              buildSaveResult({
                ok: true,
                changed,
                savedValue,
                level,
                projectName: invoice.project_name,
                clientName: invoice.client_name,
                // No sibling drafts were rewritten: going-forward only sets the
                // future default + fixes the current invoice.
                affectedNumbers: [],
              }),
            );
            setDbaEditMode('closed');
          },
        },
      );
    };

    // Step 1: correct the CURRENT invoice. For "Just this invoice" this IS the
    // whole write; for a going-forward level it corrects the invoice in front of
    // her (B2) BEFORE the default + retroactive fan-out. The write is always a
    // this_invoice cascade so the current invoice's row (establishment_id) — and
    // therefore its panel + PDF render — reflect the correction on ANY status.
    cascadeWrite.mutate(
      {
        id: invoice.id,
        data: { field: 'dba', level: 'this_invoice', establishment_id: newEstId },
      },
      {
        onError: reportError,
        onSuccess: () => {
          patchCurrentInvoiceCache();

          if (level === 'this_invoice') {
            setSaveResult(
              buildSaveResult({
                ok: true,
                changed,
                savedValue,
                level,
                projectName: invoice.project_name,
                clientName: invoice.client_name,
                affectedNumbers: [],
              }),
            );
            setDbaEditMode('closed');
            return;
          }

          // 2026-08-07 (revised): removing the DBA now CLEARS the project
          // default rather than skipping the write.
          //
          // First pass made this an early return, because
          // set_project_establishment_default raised on a NULL establishment.
          // That left a trap the moment this picker began defaulting to
          // going-forward: she picks "None (use client name)", sees a success
          // toast, and next month's invoice still carries the old DBA with no
          // way to clear it — the store could be set and never unset. The
          // backend now accepts an EXPLICIT null as a clear (allow_clear,
          // derived from model_fields_set so an omitted field still errors),
          // so "bill this project under the client name going forward" is
          // finally expressible and this falls through to the normal path.

          // Going-forward: current invoice is fixed; now set the default that
          // future invoices inherit. Existing sibling drafts are left untouched.
          applyDefault();
        },
      },
    );
  };

  // AC7 — create a new DBA under the existing client, in context, and select it.
  const createDbaInContext = (args: { tradeName: string; legalName: string | null }) => {
    addDbaInContext.mutate(
      {
        id: invoice.id,
        data: {
          trade_name: args.tradeName,
          legal_name: args.legalName,
          // Create an unused DBA first. Assigning it to an existing invoice
          // needs the reviewed, atomic invoice_assignment operation below.
          select_on_invoice: false,
        },
      },
      {
        onSuccess: (data: any) => {
          const est = data?.establishment;
          if (est?.id && invoice.organization_id) {
            setReviewIntent({ intent: 'invoice_assignment', invoice_id: invoice.id,
              organization_id: invoice.organization_id, target_organization_id: invoice.organization_id,
              target_establishment_id: est.id });
          }
          setSaveResult({ kind: 'success',
            message: `Added ${est?.name ?? 'the new DBA'} as an unused choice. Review its invoice assignment separately.`,
            affectedNumbers: [] });
          setDbaEditMode('closed');
        },
        onError: (err: any) => {
          setSaveResult({
            kind: 'error',
            message: String(err?.message ?? '') || "Couldn't add the DBA. Please try again.",
          });
        },
      },
    );
  };

  // ---------- per-row apply / propagation helpers ----------

  // R3 (invoice-identity-correctable) — AC6: the legal-entity (legal_name)
  // change CASCADES to every invoice under the DBA (the Inv 567 footgun). It is
  // deliberately NOT an in-place one-off on Christa's per-invoice flow. The edit
  // now lives on the admin/establishment screen (client settings), where it is
  // warned + shows the exact affected-invoice list before commit. On the invoice
  // the INVOICE FOR row carries only a LOWER-PROMINENCE signpost out to that
  // screen (see the INVOICE FOR row JSX below) — no in-place editor here.

  // DBA — availableModes adapts to scope_state.is_invoiced.
  // - State A (!is_invoiced): all three modes legal, default save_to_scope.
  // - State B (is_invoiced)  : drop save_to_scope, default invoice_only.
  // - State C (same value)   : no modal, just close edit (no PUT needed).
  const applyDba = (newEstId: number | null, hideOnPdf: boolean) => {
    const currentEstId = invoice.establishment_id ?? null;
    const dbaChanged = newEstId !== currentEstId;
    const hideChanged = hideOnPdf !== (invoice.hide_dba ?? false);

    if (dbaChanged && invoice.organization_id) {
      if (hideChanged) {
        setSaveResult({ kind: 'error', message: 'Save the DBA and PDF visibility setting in separate reviews.' });
        return;
      }
      setReviewIntent({ intent: 'invoice_assignment', invoice_id: invoice.id,
        organization_id: invoice.organization_id, target_organization_id: invoice.organization_id,
        target_establishment_id: newEstId });
      setEditingField(null);
      return;
    }
    if (hideChanged) {
      setReviewIntent({ intent: 'invoice_options', invoice_id: invoice.id, invoice_hide_dba: hideOnPdf });
      setEditingField(null);
      return;
    }

    if (!dbaChanged && !hideChanged) {
      setEditingField(null);
      return;
    }

    // Hide-on-PDF flips alone don't fire the propagation modal — that's a
    // per-invoice setting with no upstream column to propagate to.
    if (!dbaChanged && hideChanged) {
      // R3 (invoice-identity-cascade AC7): hide_dba is declared in
      // invoiceUpdateSchema (via invoiceCreateSchema), so this is a typed
      // Partial<InvoiceUpdate> - the prior untyped cast masked a field that was
      // actually present and is now removed. The write survives the Zod
      // boundary and persists (the response invoiceSchema also declares hide_dba).
      updateInvoiceInline.mutate({
        id: invoice.id,
        data: { hide_dba: hideOnPdf },
      });
      setEditingField(null);
      return;
    }

    // dbaChanged. Fire modal.
    const scopeIsInvoiced = scopeState?.is_invoiced ?? false;
    const availableModes: FieldUpdateMode[] = scopeIsInvoiced
      ? ['invoice_only', 'create_new_scope']
      : ['invoice_only', 'save_to_scope', 'create_new_scope'];
    const defaultMode: FieldUpdateMode = scopeIsInvoiced
      ? 'invoice_only'
      : 'save_to_scope';

    const newEst = establishments.find((e) => e.id === newEstId);
    setPending({
      field: 'establishment',
      availableModes,
      defaultMode,
      context: {
        project_name: invoice.project_name,
        organization_name: invoice.client_name,
        scope_is_invoiced: scopeIsInvoiced,
        sibling_scope_count: scopeState?.sibling_scope_count ?? 0,
      },
      oldValue: invoice.establishment_name,
      newValue: newEst?.name ?? null,
      apply: (mode) => {
        updateInvoiceInline.mutate({
          id: invoice.id,
          data: {
            establishment_id: newEstId,
            establishment_name: newEst?.name ?? null,
            // Bundle hide_dba flip into the same PUT if changed
            ...(hideChanged ? { hide_dba: hideOnPdf } : {}),
            field_update_mode: mode,
          } as any,
        });
        setEditingField(null);
      },
    });
  };

  // ADDRESS — availableModes are invoice_only / save_to_project /
  // save_to_establishment. save_to_establishment only legal when invoice has
  // an establishment.
  const applyAddress = (
    source: 'project' | 'establishment' | 'organization' | 'custom',
    customValue: string,
  ) => {
    const same = source === (invoice.billing_address_source ?? 'organization')
      && (source !== 'custom' || customValue === (invoice.billing_address_override ?? ''));
    addressSaveInFlightRef.current = false;
    if (same) { setEditingField(null); return; }
    const reviewIntentForAddress = (): InvoiceChangeIntent => source === 'custom'
      ? { intent: 'invoice_override', invoice_id: invoice.id, fields: { billing_address: customValue } }
      : { intent: 'invoice_options', invoice_id: invoice.id, invoice_billing_address_source: source };
    const saveThisInvoiceAddress = async () => {
      // Draft is only an affordance. The server checks actual issuance evidence
      // and rejects this direct write with review_required when needed.
      if (invoice.status !== 'draft') {
        setReviewIntent(reviewIntentForAddress());
        return;
      }
      try {
        const saved = await api.invoices.cascadeWrite(invoice.id, {
          field: 'billing_address', level: 'this_invoice',
          billing_address_source: source,
          billing_address_override: source === 'custom' ? customValue : null,
        });
        if (!saved.applied) throw new Error('The invoice address was not saved.');
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['invoices', invoice.id] }),
          queryClient.invalidateQueries({ queryKey: ['invoices', invoice.id, 'full'] }),
          queryClient.invalidateQueries({ queryKey: ['invoices', invoice.id, 'document-history'] }),
        ]);
        setSaveResult({ kind: 'success', message: 'Saved this invoice’s billing address. Future defaults and other invoices were unchanged.', affectedNumbers: [] });
      } catch (error) {
        const detail = (error as { data?: { error?: { code?: string }; detail?: { code?: string } } }).data;
        if (detail?.error?.code === 'review_required' || detail?.detail?.code === 'review_required') {
          setReviewIntent(reviewIntentForAddress());
        } else {
          setSaveResult({ kind: 'error', message: `Address not saved: ${error instanceof Error ? error.message : 'please try again'}` });
        }
      } finally {
        addressSaveInFlightRef.current = false;
      }
    };
    if (source === 'custom') {
      const modes: FieldUpdateMode[] = [
        'invoice_only',
        ...(invoice.project_id && invoice.client_scope_id ? ['save_to_project' as const] : []),
        ...(invoice.organization_id ? ['save_to_organization' as const] : []),
        ...(invoice.establishment_id && invoice.organization_id ? ['save_to_establishment' as const] : []),
      ];
      setPending({ field: 'address', availableModes: modes, defaultMode: 'invoice_only',
        context: { project_name: invoice.project_name ?? undefined,
          establishment_name: invoice.establishment_name ?? undefined,
          organization_name: invoice.client_name ?? undefined },
        oldValue: addressValue, newValue: customValue,
        modeNotes: {
          invoice_only: 'Save this invoice only. Future billing addresses stay unchanged.',
          save_to_project: 'Review this client billing track as a future project address default; existing invoices remain separately selectable.',
          save_to_organization: 'Review the client-wide future billing address default; existing invoices remain separately selectable.',
          save_to_establishment: 'Review the DBA billing address source; existing invoices remain separately selectable.',
        },
        apply: (mode) => {
          if (mode === 'save_to_project' && invoice.project_id && invoice.client_scope_id && invoice.organization_id) {
            setReviewIntent({ intent: 'identity_default', organization_id: invoice.organization_id,
              default_level: 'this_project', default_field: 'billing_address', future_defaults: true,
              focused_invoice_id: invoice.id,
              project_ids: [invoice.project_id], client_scope_ids: [invoice.client_scope_id],
              default_billing_address_source: 'custom', default_billing_address_override: customValue });
          } else if (mode === 'save_to_organization' && invoice.organization_id) {
            setReviewIntent({ intent: 'identity_default', organization_id: invoice.organization_id,
              default_level: 'this_client', default_field: 'billing_address', future_defaults: true,
              focused_invoice_id: invoice.id,
              default_billing_address_source: 'custom', default_billing_address_override: customValue });
          } else if (mode === 'save_to_establishment' && invoice.organization_id && invoice.establishment_id) {
            setReviewIntent({ intent: 'shared_correction', organization_id: invoice.organization_id,
              establishment_id: invoice.establishment_id, fields: { billing_address: customValue } });
          } else {
            void saveThisInvoiceAddress();
          }
          closePending();
        },
      });
    } else {
      void saveThisInvoiceAddress();
    }
    setEditingField(null);
  };
  // A recipient row that can be sent to the project pin. Only the id matters
  // here: whether the contact still EXISTS is verified server-side in
  // set_project_recipient_set, which is the only layer that can see it.
  //
  // An earlier version of this also tried to exclude "(removed contact)" rows
  // via `source === 'user_added' && !name && !email`. That predicate is inert:
  // get_invoice_contacts_meta classifies a deleted contact as `snapshot_org`
  // and still renders its name and email from the NOT NULL snapshot columns
  // (V150), while `user_added` actually means a contact scoped to a DIFFERENT
  // DBA — which is legitimately pinnable. So it excluded the wrong rows and
  // caught none of the right ones.
  const pinnable = (r: RecipientMeta) => !!r.contact_id;

  // R2 AC13: change TO or CC for a person already on this invoice. Not on a sent or paid invoice: those recipients
  // are a record of who it went to, so the row keeps its display-only badge there.
  const canChangeRole = isEditable && !ROLE_LOCKED_STATUSES.includes(invoice.status ?? '') && !isSent(invoice.send_evidence);
  const projectLabelForRoles = [invoice.project_number, invoice.project_name].filter(Boolean).join(' ') || 'this project';
  // Terminal cycle 2 LOW: "Save for this project" is on every recipient row that is not already one of the project's saved
  // people (typed-in one-offs AND people picked from the client's list) on eligible unsent invoices.
  // A row with no email cannot be saved. The star dialog gets this same availability for its guidance.
  const canSaveForProject = (row: RecipientMeta) =>
    canChangeRole && !!invoice.client_scope_id && !!row.invoice_contact_id && !!(row.email ?? '').trim()
    && (row.source === 'typed_in' || (!!row.contact_id && row.is_project_default !== true));
  const chooseRole = (row: RecipientMeta, next: Role) => {
    if (!row.invoice_contact_id || next === row.role) return;
    setRoleRefusal(null);
    // An invoice needs a TO: refuse before any request when this is the last one.
    if (row.role === 'to' && next === 'cc' && !recipientsMeta.some((r) => r.role === 'to' && r.invoice_contact_id !== row.invoice_contact_id)) {
      setRoleRefusal('An invoice needs at least one TO. Make someone else TO first.');
      return;
    }
    const saved = !!invoice.client_scope_id && !!row.contact_id && row.is_project_default === true;
    setRoleChange({
      rowId: row.invoice_contact_id,
      personName: row.name || row.email || 'This person',
      newRole: next,
      savedForProject: saved ? projectLabelForRoles : null,
    });
  };

  // Who is already on this invoice, by contact id and by email (trimmed, ignoring case: a typed-in person has
  // no contact id, and the same address typed in another case is still the same person). Used by the picker
  // and the typed-in form so nobody is offered who cannot be added; the server refuses a duplicate too.
  const idsOnInvoice = new Set(recipientsMeta.map((r) => r.contact_id).filter((v): v is string => !!v));
  const emailsOnInvoice = new Set(
    recipientsMeta.map((r) => (r.email ?? '').trim().toLowerCase()).filter(Boolean),
  );
  const isOnInvoice = (id: string, email: string) =>
    idsOnInvoice.has(id) || (!!email && emailsOnInvoice.has(email.trim().toLowerCase()));
  const typedEmailIsOnInvoice =
    !!oneOffEmail.trim() && emailsOnInvoice.has(oneOffEmail.trim().toLowerCase());

  const renderPoolGroup = (
    heading: string,
    contacts: EligibleContact[],
    emailOf: (contact: EligibleContact) => string,
  ) => (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{heading}</p>
      <ul className="space-y-1">
        {contacts.map((c) => {
          const email = emailOf(c);
          const already = isOnInvoice(c.id, email);
          return (
            <li key={c.id} className="flex items-center justify-between gap-2">
              <span className="text-sm truncate">
                {c.name}
                {email && <span className="text-slate-500 ml-1">{email}</span>}
              </span>
              <button
                type="button"
                className="text-xs text-blue-700 hover:underline disabled:text-slate-500 disabled:no-underline"
                data-testid={`bill-to-add-recipient-from-pool-${c.id}`}
                disabled={already}
                aria-label={already ? `${c.name} is already on this invoice` : undefined}
                onClick={() =>
                  applyAddRecipient({ kind: 'from_pool', contact_id: c.id, name: c.name, email })
                }
              >
                {already ? 'Already on this invoice' : 'Add'}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );

  // RECIPIENT — add
  //
  // SPEC-christa-contacts-identity R1 AC7-AC12. A person she TYPES IN and a person she PICKS get the same
  // choices, in this order, with the project option pre-selected when the invoice belongs to a project:
  //
  //   This project, going forward  -> saves the person for THIS project only (one request)
  //   Save as default for {Client}  -> every project of the client that has no contacts of its own
  //   Just this invoice
  //
  // Her words, 2026-09-29: "Can we add a prompt that says 'add for this project?' example I put email contact
  // in and had to say just for this ivoice b/c the only other optoins was something along the lines of for all
  // invoices realated to Quay managmet moving forwrad." The project option used to be withheld from a typed-in
  // person because it was two writes (add, then a cascade keyed on a contact id) and the second could not run
  // without one. It is ONE request now: the server finds or creates the client contact, adds the invoice row
  // and pins the project's people in a single transaction, so the option is honest for a new person too and a
  // failure leaves nothing behind. The modal also asks TO or CC, and lists exactly who will be saved for the
  // project so she can untick anyone (people typed for this invoice only are never saved).
  const applyAddRecipient = (
    args:
      // `role` is optional: the modal's role choice decides it (TO when the invoice has no TO yet, CC after).
      | { kind: 'from_pool'; contact_id: string; name: string; email: string; role?: 'to' | 'cc' }
      | { kind: 'typed_in'; name: string; email: string; role?: 'to' | 'cc' },
  ) => {
    // A brand-new person has no contact id yet; the server keys the create-the-contact path on that.
    const _contactId: string | undefined =
      args.kind === 'from_pool' ? args.contact_id : undefined;

    // Gated on the CLIENT SCOPE (the project's payer), not the establishment: the project option needs no DBA,
    // and gating it on one hid the only sticky option from the 518 of 566 clients that have none.
    const hasProjectScope = !!invoice.client_scope_id;
    const availableModes: FieldUpdateMode[] = hasProjectScope
      ? ['save_project_default', 'save_org_default', 'invoice_only']
      : ['invoice_only', 'save_org_default'];
    // The project is what she asked for, for a typed-in person and a picked one alike. Without a project the
    // reversible option stays the default, so nobody lands on the client by accident.
    const defaultMode: FieldUpdateMode = hasProjectScope ? 'save_project_default' : 'invoice_only';
    const projectLabel = hasProjectScope
      ? [invoice.project_number, invoice.project_name].filter(Boolean).join(' ') || 'this project'
      : undefined;
    const toRole = (r: string): 'to' | 'cc' => (r === 'to' ? 'to' : 'cc');
    const recipientAdd: RecipientAddInfo = {
      personName: args.name,
      personEmail: args.email,
      projectLabel,
      defaultRole: recipientsMeta.some((r) => r.role === 'to') ? 'cc' : 'to',
      savedPeople: recipientsMeta
        .filter((r) => pinnable(r) && r.contact_id !== _contactId)
        .map((r) => ({
          contact_id: r.contact_id as string,
          name: r.name ?? '',
          email: r.email ?? '',
          role: toRole(r.role),
        })),
      // R2 AC14: a typed-in person WITH an email can be saved for the project too (ticked by default); only one
      // with no email stays on this invoice only.
      oneOffPeople: recipientsMeta
        .filter((r) => !pinnable(r) && !!r.invoice_contact_id && !!(r.email ?? '').trim())
        .map((r) => ({ row_id: r.invoice_contact_id as string, name: r.name ?? '', email: r.email ?? '', role: toRole(r.role) })),
      invoiceOnlyPeople: recipientsMeta
        .filter((r) => !pinnable(r) && !(r.invoice_contact_id && (r.email ?? '').trim()))
        .map((r) => ({ name: r.name ?? '', email: r.email ?? '', role: toRole(r.role) })),
    };

    setPending({
      field: 'recipient_add',
      availableModes,
      defaultMode,
      context: {
        project_name: invoice.project_name ?? undefined,
        establishment_name: invoice.establishment_name,
        organization_name: invoice.client_name,
      },
      newValue: `${args.name} <${args.email}>`,
      recipientAdd,
      apply: (mode: FieldUpdateMode, extras?: PropagationConfirmExtras) => {
        const role = extras?.role ?? args.role;
        // ONE request for every option, project included: no second call to the cascade.
        addContact.mutate(
          {
            invoiceId: invoice.id,
            data: {
              contact_id: _contactId,
              contact_name: args.name,
              contact_email: args.email,
              ...(role ? { role } : {}),
              field_update_mode: mode as
                | 'invoice_only'
                | 'save_dba_default'
                | 'save_org_default'
                | 'save_project_default',
              ...(mode === 'save_project_default' && extras?.projectSetContactIds
                ? { project_set_contact_ids: extras.projectSetContactIds }
                : {}),
              ...(mode === 'save_project_default' && extras?.projectSetRowIds?.length
                ? { project_set_row_ids: extras.projectSetRowIds }
                : {}),
            },
          },
          {
            // Re-render the recipients list and clear the empty-state warning without a reload.
            onSuccess: () => {
              invalidateRecipients();
            },
          },
        );
        setEditingField(null);
      },
    });
  };

  // RECIPIENT: save a one-off for the project (R2 AC14)
  //
  // A person typed in as a one-off earlier ("Just this invoice") can be saved for the project from their own row.
  // The confirmation is the same project-save list the add flow uses, with the person already chosen (ticked, not
  // untickable) at the role they have, every saved person ticked, and every other typed-in person with an email
  // tickable too. One request promotes them in place and pins the project's people; nothing else is changed.
  const applyPromoteToProject = (target: RecipientMeta) => {
    if (!target.invoice_contact_id) return;
    const toRole = (r: string): 'to' | 'cc' => (r === 'to' ? 'to' : 'cc');
    setPending({
      field: 'recipient_add',
      availableModes: ['save_project_default'],
      defaultMode: 'save_project_default',
      context: {
        project_name: invoice.project_name ?? undefined,
        establishment_name: invoice.establishment_name ?? undefined,
        organization_name: invoice.client_name ?? undefined,
      },
      newValue: `${target.name ?? ''} <${target.email ?? ''}>`,
      recipientAdd: {
        personName: target.name ?? '',
        personEmail: target.email ?? '',
        projectLabel: projectLabelForRoles,
        defaultRole: toRole(target.role),
        promote: { rowId: target.invoice_contact_id, role: toRole(target.role) },
        savedPeople: recipientsMeta
          .filter((r) => pinnable(r) && r.invoice_contact_id !== target.invoice_contact_id)
          .map((r) => ({ contact_id: r.contact_id as string, name: r.name ?? '', email: r.email ?? '', role: toRole(r.role) })),
        oneOffPeople: recipientsMeta
          .filter((r) => !pinnable(r) && !!r.invoice_contact_id && r.invoice_contact_id !== target.invoice_contact_id
            && !!(r.email ?? '').trim())
          .map((r) => ({ row_id: r.invoice_contact_id as string, name: r.name ?? '', email: r.email ?? '', role: toRole(r.role) })),
        invoiceOnlyPeople: recipientsMeta
          .filter((r) => !pinnable(r) && !(r.invoice_contact_id && (r.email ?? '').trim()))
          .map((r) => ({ name: r.name ?? '', email: r.email ?? '', role: toRole(r.role) })),
      },
      apply: (_mode: FieldUpdateMode, extras?: PropagationConfirmExtras) => {
        promoteContact.mutate({
          invoiceId: invoice.id,
          rowId: target.invoice_contact_id as string,
          body: {
            target: 'project',
            project_set_contact_ids: extras?.projectSetContactIds ?? [],
            project_set_row_ids: extras?.projectSetRowIds ?? [],
          },
        });
        setEditingField(null);
      },
    });
  };

  // RECIPIENT — remove
  const applyRemoveRecipient = (row: RecipientMeta) => {
    if (!row.invoice_contact_id) return;
    // Typed-in one-offs have no org_contact row to flip — surface only the
    // invoice_only path. (Backend's stop_for_dba/stop_for_org no-op gracefully
    // when contact_id is null, but skipping the prompt is the kinder UX.)
    if (!row.contact_id) {
      removeContact.mutate({
        invoiceId: invoice.id,
        rowId: row.invoice_contact_id,
        fieldUpdateMode: 'invoice_only',
      });
      return;
    }
    // Gated on the client SCOPE, matching the add path: stop_for_dba now also
    // deletes from project_scope_recipient_defaults via the cascade, which
    // anchors on client_scope_id and needs no establishment.
    //
    // The DEFAULT stays invoice_only, deliberately asymmetric with the add. She
    // has asked in writing for contacts to carry FORWARD (2026-06-05, inv 586);
    // there is no quote asking a REMOVAL to apply forward, and a sticky remove
    // silently drops a contact from every future invoice on the project. Offer
    // it, do not choose it for her.
    const hasProjectScope = !!invoice.client_scope_id;
    // The project level was WITHHELD here for one day (2026-08-08 morning),
    // because a project-level remove pins the REMAINING recipients and, with
    // one recipient on the invoice, that set is empty -- and an empty pin was
    // indistinguishable from never-pinned, so resolution fell through to the
    // org pool and handed her back the person she just removed. Withholding it
    // hurt: 43 of the 48 invoices carrying recipients have exactly one (prod
    // audit 2026-08-08), so the option disappeared on nine removes in ten.
    //
    // V211 gives the empty pin a place to live (default_recipients_cleared), so
    // "nobody on this project going forward" is now a durable answer and the
    // option is offered again on any invoice with a client scope.
    const availableModes: FieldUpdateMode[] =
      hasProjectScope
        ? ['invoice_only', 'stop_for_dba', 'stop_for_org']
        : ['invoice_only', 'stop_for_org'];
    setPending({
      field: 'recipient_remove',
      availableModes,
      defaultMode: 'invoice_only',
      // Say what the going-forward option ACTUALLY does when this is the last
      // recipient: it is not just "stop including this person", it records a
      // deliberate clear, so no future invoice on the project gets automatic
      // recipients and the org-Primary Attention fallback is off for them too.
      // Only shown when that is the case, so it does not cry wolf on the
      // ordinary multi-recipient removal.
      modeNotes:
        recipientsMeta.filter((r) => r.invoice_contact_id !== row.invoice_contact_id)
          .length === 0
          ? {
              stop_for_dba:
                'This is the last recipient, so future {project_name} invoices ' +
                'will have none - and they will not fall back to the ' +
                'client Primary contact either.',
            }
          : undefined,
      context: {
        project_name: invoice.project_name ?? undefined,
        establishment_name: invoice.establishment_name,
        organization_name: invoice.client_name,
      },
      oldValue: `${row.name ?? ''} <${row.email ?? ''}>`,
      apply: (mode) => {
        // 2026-08-07: the project-level REMOVE is composed, mirroring the add.
        //
        // Without this the project recipient default is a ONE-WAY DOOR: the add
        // path writes project_scope_recipient_defaults, the resolver returns it
        // as precedence step 1, and nothing in the product ever deletes the row.
        // She removes the contact from an invoice, and next month it is back,
        // permanently — the same "thought it stuck but guess not" loop pointing
        // the other way (2026-06-23, inv 609), with no workaround at all.
        //
        // `set_project_recipient_default(add=False)` already existed and had no
        // caller. /contacts' own stop_for_dba only flips the org_contact
        // accounting flag; it never touches the project store.
        const isProjectLevel = mode === 'stop_for_dba';
        removeContact.mutate(
          {
            invoiceId: invoice.id,
            rowId: row.invoice_contact_id!,
            // 2026-08-08: send invoice_only for the PROJECT level, exactly as
            // the add path does.
            //
            // /contacts' own stop_for_dba flips organization_contacts
            // .is_accounting_contact FALSE for this contact on this
            // ESTABLISHMENT — which removes them from the org accounting pool
            // for EVERY other project billing under that DBA. The option is
            // labelled "This project, going forward". A control that says this
            // project and writes DBA-wide is the sibling-destroying behaviour
            // she has objected to three times, most plainly 2026-05-28 (inv
            // 567/447): "it should only be the same legal entity in this
            // particular project (as 447)". The cascade below owns the project
            // half; the org-flag flip stays on stop_for_org, where the label
            // says so.
            fieldUpdateMode: (isProjectLevel ? 'invoice_only' : mode) as
              | 'invoice_only'
              | 'stop_for_dba'
              | 'stop_for_org',
          },
          {
            // CHAINED, not parallel. Fired side by side, the project delete
            // could commit while the invoice-level remove failed (a payment
            // lock — inv 0619, 2026-08-04), leaving her looking at a recipient
            // that is still on this invoice and silently gone from every future
            // one. The add path already chains; this is the same hazard the
            // atomic writer was built to kill, surviving on the sibling path.
            onSuccess: () => {
              invalidateRecipients();
              if (!isProjectLevel || !row.contact_id) return;
              // Pin the REMAINING set, rather than deleting one row.
              //
              // Deleting one row only does something when a set was already
              // pinned — which is no scope in production — so a removal
              // reported success and changed nothing, and the contact came back
              // next month from the org pool. That is 2026-06-05 (inv 586)
              // verbatim: "It shows up now as going to Dana Rocca and I can't
              // remove it… I fear it didn't take in the system." Writing the
              // remaining set makes a removal durable on FIRST use, and keeps
              // the store's meaning identical on both paths: these are the
              // recipients for this project.
              const survivors = recipientsMeta.filter(
                (r) => r.invoice_contact_id !== row.invoice_contact_id,
              );
              const remaining = survivors
                .filter((r) => pinnable(r))
                .map((r) => ({
                  contact_id: r.contact_id as string,
                  role: (r.role === 'to' ? 'to' : 'cc') as 'to' | 'cc',
                }));
              // An empty `remaining` is only a DELIBERATE clear when the invoice
              // is genuinely empty afterwards.
              //
              // pinnable() is `!!contact_id`, so a TYPED-IN one-off (contact_id
              // null — she typed accounts@artigiano.ca by hand) is filtered out
              // of `remaining` even though it survives on the invoice. Removing
              // the OTHER recipient then sent recipient_set: [], which
              // set_project_recipient_set records as "deliberately nobody on
              // this project" — and, since 2026-08-08, that also blanks the PDF
              // Attention block on every future invoice. She asked to stop
              // sending to one person and got silence for everybody.
              //
              // The backend comment says the flag is keyed on what she ASKED
              // FOR rather than what survived filtering; that is true of the
              // backend's own filter and was NOT true of this one, because this
              // filter runs first. So: if anything survives on the invoice that
              // simply cannot be pinned, this is not a clear, and we do not send
              // one.
              const clearsTheProject = survivors.length === 0;
              // The PDF's real gate is "no surviving role='to' row", not "no
              // surviving rows". _resolve_to_contacts builds to_contacts from
              // invoice_contacts WHERE role='to' and falls back to the org
              // Primary whenever that list is empty -- so removing the last TO
              // while a CC remains still prints the Primary, who may be the
              // person she just removed. survivors.length === 0 is a strict
              // subset of that, so gating the suppression on it left the common
              // TO+CC shape leaking.
              const aToSurvives = survivors.some((r) => r.role === 'to');
              // And silence the Attention block on THIS invoice too.
              //
              // V211 sets suppress_attention_line at CREATE, which fixes next
              // month and leaves the invoice she is standing on unchanged: zero
              // invoice_contacts with suppress=false is exactly the branch
              // _resolve_to_contacts falls through to the org Primary on. So she
              // removes Dana, the row disappears, she previews the PDF — and
              // Dana is still in the Attention block, with no control anywhere
              // that changes it.
              //
              // That is inv 586 for the CURRENT invoice, which is the half the
              // V211 work quoted as its reason and did not close. Only when the
              // invoice genuinely empties: an invoice that still has a typed-in
              // recipient must keep rendering it.
              if (!aToSurvives) {
                updateInvoiceInline.mutate({
                  id: invoice.id,
                  data: { suppress_attention_line: true },
                });
              }
              // 2026-08-08: an EMPTY remaining set cannot express "stop
              // including this person".
              //
              // `_resolve_recipients` tests the project set with `if proj:` —
              // an empty set is falsy, indistinguishable from never-pinned — so
              // resolution falls straight through to the org accounting pool,
              // which still contains her. Removing the LAST recipient on
              // "This project, going forward" would therefore write the pin
              // away, report success, and hand the same person back next month.
              // That is 2026-06-05 (inv 586) exactly: "It shows up now as going
              // to Dana Rocca and I can't remove it… I fear it didn't take in
              // the system."
              //
              // That WAS the state of things until V211. The paragraph above is
              // kept as the reason this path exists; it is no longer the reason
              // it behaves as it does. default_recipients_cleared makes the
              // empty pin durable, so the write below holds and the warning that
              // used to hedge about it is gone (see the onSuccess note).
              cascadeWrite.mutate(
                {
                  id: invoice.id,
                  data: {
                    field: 'recipients',
                    level: 'this_project',
                    // Never an empty set unless the invoice really is empty —
                    // see clearsTheProject above.
                    recipient_set:
                      remaining.length === 0 && !clearsTheProject
                        ? undefined
                        : remaining,
                    ...(remaining.length === 0 && !clearsTheProject
                      ? { contact_id: row.contact_id, recipient_add: false }
                      : {}),
                  },
                },
                {
                  // This warning covered TWO cases and V211 only fixed one.
                  //
                  // Fixed: a deliberately-empty pin used to fall through to the
                  // org pool. It is durable now, so a remove that empties the
                  // pin holds and warning about it would be the same defect in
                  // reverse -- telling her a save failed when it worked.
                  //
                  // NOT fixed: set_project_recipient_set drops contact_ids whose
                  // organization_contact was deleted (they would FK-violate).
                  // default_recipients_cleared is keyed on what she ASKED FOR,
                  // deliberately -- so a non-empty request whose contacts were
                  // all deleted stores nothing AND records no intent, and
                  // resolution falls back to the org pool. That is the silent
                  // loss the warning was built for, and it is exactly her
                  // "(removed contact)" shape (2026-08-04, inv 649).
                  //
                  // So it fires on that case only: we sent names, none landed.
                  onSuccess: (resp: any) => {
                    // The single-contact branch (a typed-in one-off survives, so
                    // we cannot send a set) deletes from a pin that 311 of 317
                    // scopes do not have. When it changes nothing, the removal
                    // did NOT carry forward -- and reporting a clean success
                    // over that is inv 609 exactly: "changed it last time and
                    // thought it stuck but guess not!!"
                    if (resp?.recipient_default_changed === false) {
                      toast.warning(
                        `${row.name ?? 'That recipient'} is off invoice ` +
                          `${invoice.invoice_number}, but nothing was saved for ` +
                          `future invoices on this project - there were no saved ` +
                          `recipients to remove them from. Add the people you DO ` +
                          `want on this project to make it stick.`,
                        { duration: Infinity },
                      );
                      return;
                    }
                    const stored =
                      typeof resp?.recipients_written === 'number'
                        ? resp.recipients_written
                        : remaining.length;
                    if (stored === 0 && remaining.length > 0) {
                      toast.warning(
                        `${row.name ?? 'That recipient'} is off invoice ` +
                          `${invoice.invoice_number}, but none of the remaining ` +
                          `recipients could be saved for this project: their ` +
                          `contact records have been deleted. The next invoice ` +
                          `will use this client's saved contacts or copy the ` +
                          `last one you sent, so check who it goes to.`,
                        { duration: Infinity },
                      );
                    }
                  },
                  onError: (err: any) => {
                    toast.error(
                      `${row.name ?? 'That recipient'} was removed from this ` +
                        `invoice, but the recipients could not be saved for ` +
                        `future invoices on this project` +
                        (err?.message ? `: ${err.message}` : '.'),
                      { duration: Infinity },
                    );
                  },
                },
              );
            },
          },
        )
      },
    });
  };

  // RECIPIENT — star toggle
  //
  // R8c follow-up — Fix #1: align `isFilled` with the visual star state.
  // The star in the list (line ~990) renders filled when EITHER
  // is_dba_accounting OR is_org_accounting is true. The previous version of
  // this handler only checked is_dba_accounting, so a contact who was
  // org-level accounting (filled star, aria-label "Remove …") routed through
  // the ADD modal copy ("Save Anna as accounting contact for {DBA}") —
  // Christa's "why does Remove turn into Save?" stumble in R8c Phase 5.
  //
  // Per spec:
  //   Filled → outlined click: starToggleAction='remove_from_dba_default',
  //                            availableModes=['stop_for_dba']
  //   Outlined → filled click: starToggleAction='add_as_dba_default',
  //                            availableModes=['save_dba_default']
  // Source: R8c-final-closeout-2026-05-27.md Phase 5 #5 + Phase 3 finding #2.
  const applyStarToggle = (row: RecipientMeta) => {
    if (!row.contact_id) return; // typed-in / dangling FK → no toggle target
    // Visual-aligned: star is filled if the contact has accounting flag at
    // EITHER scope (matches what the user sees, matches the aria-label).
    const isFilled = row.is_dba_accounting || row.is_org_accounting;
    // Scope strategy: if invoice has an establishment, toggle at DBA scope;
    // otherwise toggle at org scope.
    const scope: 'dba' | 'org' = invoice.establishment_id ? 'dba' : 'org';
    const newValue = !isFilled;

    // Direction-aware modal contract. The PropagationModal renders different
    // title + body copy based on starToggleAction; availableModes carries
    // the underlying FieldUpdateMode the modal will report on Apply.
    const starToggleAction: 'add_as_dba_default' | 'remove_from_dba_default' =
      isFilled ? 'remove_from_dba_default' : 'add_as_dba_default';
    const availableModes: FieldUpdateMode[] = isFilled
      ? ['stop_for_dba']
      : ['save_dba_default'];
    const defaultMode: FieldUpdateMode = availableModes[0];

    setPending({
      field: 'star_toggle',
      availableModes,
      defaultMode,
      context: {
        establishment_name: invoice.establishment_name,
        organization_name: invoice.client_name,
      },
      oldValue: `${row.name ?? ''} ${isFilled ? '★' : '☆'}`,
      // newValue carries the recipient's name so the modal copy can
      // substitute {newValue} in both directional titles + bodies.
      newValue: row.name ?? '',
      starToggleAction,
      starProjectLabel: invoice.client_scope_id && recipientsMeta.some((r) => r.project_has_own_people)
        ? projectLabelForRoles : undefined,
      starProjectSaveState: row.is_project_default ? 'already_saved'
        : !(row.email ?? '').trim() ? 'missing_email'
          : canSaveForProject(row) ? 'available'
            : !canChangeRole ? 'invoice_locked' : 'unavailable',
      apply: () => {
        toggleDefault.mutate({
          invoiceId: invoice.id,
          contactId: row.contact_id!,
          data: { toggle_to: newValue, scope },
        });
      },
    });
  };

  // ---------- render ----------

  return (
    <section
      data-testid="bill-to-section"
      className="bg-white border border-slate-200 rounded-md text-[15px]"
    >
      {/* Header bar with optional Send Preview button */}
      <header className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <h2 className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
          Bill To
        </h2>
        {onSendPreview && (
          <button
            type="button"
            onClick={onSendPreview}
            className="text-xs text-blue-700 hover:underline"
            data-testid="bill-to-send-preview"
          >
            Send preview &#9656;
          </button>
        )}
      </header>

      <div className="px-4 py-3 divide-y divide-slate-100">
        {/* ============ CLIENT (Wave 3 v0.14 — org-change) ============
            Always-first row per §8 lock. The visible action opens the invoice-only review.
            Per §0.2: "Client" label, not "Organization." */}
        <div className="py-3" data-testid="bill-to-row-client">
          <FieldLabel>Billing client</FieldLabel>
          <div className="flex flex-col items-start gap-2 sm:flex-row">
            <div className="flex-1 min-w-0">
              <p className={VALUE_CLS} data-testid="bill-to-client-value">
                {clientValue || (
                  <span className="text-slate-400 italic">Not set</span>
                )}
              </p>
              <p className={SOURCE_CLS} data-testid="bill-to-client-source">
                {clientSourceCaption}
              </p>
            </div>
            {/* R3 (invoice-identity-correctable): always-enabled, on ANY status
                incl. SENT. The freeze/lock is dropped — Christa corrects the
                client on the invoice in front of her, no "return to draft"
                dead-end (AC5). */}
            <button
              type="button"
              className="inline-flex min-h-9 items-center justify-center rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600"
              aria-label="Change on this invoice only"
              title="Change on this invoice only"
              data-testid="bill-to-client-edit"
              onClick={() => setOrgChangeOpen(true)}
            >
              Change on this invoice
            </button>
          </div>
        </div>

        {/* R2 AC1: the link opens the named-project payer flow, which changes
            future invoices (and existing invoices she chooses), unlike the
            invoice-only action above. */}
        {invoice.project_id && invoice.organization_id && <button
          type="button"
          className="my-2 min-h-9 text-xs text-slate-600 underline underline-offset-2 hover:text-slate-900"
          onClick={() => setProjectPayerOpen(true)}
          data-testid="bill-to-review-named-project-payer"
        >
          Change the client for this project
        </button>}

        {/* ============ INVOICE FOR (legal entity) ============ */}
        {/* R3 (invoice-identity-correctable) — AC6: the legal-entity name is the
            CASCADES-TO-ALL edit (changing it rewrites the legal name on every
            existing invoice under this DBA — the Inv 567 footgun that scared
            Christa). It is deliberately NOT an in-place one-off on her
            per-invoice flow. Show the resolved value + a LOWER-PROMINENCE
            signpost out to client settings (mirrors the "Edit in project"
            signpost — G2/AC3), where the change is warned + shows the exact
            affected-invoice list before it commits (see EstablishmentsList).
            The routine one-offs (DBA / billing address / recipients) stay
            in-place below. On NULL-DBA invoices the signpost is hidden (the
            legal entity rides with a DBA); a quiet hover/focus hint points her
            to the client row / add-DBA instead. */}
        <div
          className="py-3 group"
          data-testid="bill-to-row-legal-name"
          tabIndex={isEditable && !invoice.establishment_id ? 0 : undefined}
        >
          <FieldLabel>Invoice For</FieldLabel>
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <p className={VALUE_CLS} data-testid="bill-to-legal-name-value">
                {legalNameValue || <span className="text-slate-400 italic">No legal name</span>}
              </p>
              {showLegalNameSource && (
                <p className={SOURCE_CLS} data-testid="bill-to-legal-name-source">
                  {legalNameSource}
                </p>
              )}
            </div>
            {/* AC6 de-emphasis: a lower-prominence signpost link (NOT the
                equal-weight pencil the DBA/address one-offs use) out to the
                admin/establishment screen where the cascade edit lives. */}
            {isEditable && invoice.establishment_id && invoice.organization_id && (
              <Link
                to={`/organizations/${invoice.organization_id}/edit`}
                className="text-sm text-blue-700 hover:underline inline-flex items-center gap-1 flex-shrink-0"
                data-testid="bill-to-legal-name-signpost"
                title="Change the legal name in client settings"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Change in client settings
              </Link>
            )}
          </div>
          {/* AC6: name the cascade right on the invoice so she understands WHY
              the legal name is not a quick one-off here (and where it lives). */}
          {isEditable && invoice.establishment_id && (
            <p
              className="mt-1 text-xs text-slate-500"
              data-testid="bill-to-legal-name-cascade-note"
            >
              Review legal-name changes in client settings and choose which invoices to update.
            </p>
          )}
          {/* Christa-friction-polish CRIT-2: on NULL-DBA invoices (83% of
              prod), the signpost is intentionally hidden (the legal entity
              rides with a DBA). Without a hint, Christa reads "nothing here =
              broken." Reveal on hover/focus only so the panel stays quiet by
              default but the affordance shows up when she's actively looking. */}
          {isEditable && !invoice.establishment_id && (
            <p
              className="mt-1 text-xs text-slate-500 italic opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
              data-testid="bill-to-legal-name-no-dba-hint"
            >
              To change the legal entity name, edit the client above or add a DBA first.
            </p>
          )}
        </div>

        {/* ============ DBA ============ */}
        {/* R4 (invoice-identity-cascade): the DBA row is the primary Christa-Land
            cascade surface. Editing opens the InlineCascadePicker IN PLACE (no
            modal, no kebab — AC2/AC9); the source caption is now driven by R2's
            resolution (AC1/AC5); the consequence-preview radios carry counts
            (AC2); the default level is context-aware (AC3); the post-save
            confirmation is evidentiary (AC4/AC8). The legacy applyDba/Propagation
            path is retained for the hide-on-PDF toggle + the no-resolution
            fallback, but the resolution-driven picker is the forward surface. */}
        <div className="py-3" data-testid="bill-to-row-dba">
          <div className="flex items-start gap-1">
            <FieldLabel>Trading name (DBA)</FieldLabel>
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" aria-label="About trading names (DBA)"
                  className="inline-flex h-6 w-6 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600">
                  <span aria-hidden="true" className="text-xs font-semibold">?</span>
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="max-w-[calc(100vw-2rem)] text-sm">
                A trading name is the name a business operates under. It can differ from its legal name.
              </PopoverContent>
            </Popover>
          </div>
          {isEditable && dbaEditMode === 'add_dba' ? (
            /* AC7 — easy in-context add-DBA mini-form. */
            <AddDbaInline
              clientName={invoice.client_name}
              pending={addDbaInContext.isPending}
              onCreate={createDbaInContext}
              onCancel={() => setDbaEditMode('closed')}
            />
          ) : isEditable && dbaEditMode === 'picker' ? (
            /* AC2/AC3/AC4 — inline cascade picker (no modal). */
            <InlineCascadePicker
              fieldLabel="DBA"
              clientName={invoice.client_name}
              projectName={invoice.project_name}
              invoiceNumber={invoice.invoice_number}
              projectDraftCount={projectDraftCount}
              clientInvoiceCount={clientInvoiceCount}
              multiClientProject={multiClientProject}
              editReason={dbaEditReason}
              // A project-level default needs a client scope to land on; without
              // one the write raises (AC5). Fall back to the one-off so the
              // pre-selected option can never be a Save that fails.
              hasProjectScope={!!invoice.client_scope_id}
              // Surface the CLIENT level while she is hiding the DBA. Her ask
              // (inv 618) is client-wide and Living Balance is a single-client
              // project, so the multiClientProject gate hid the only level she
              // wanted. Scoped to the hide flip -- a DBA/legal-entity change
              // keeps the burial, because that one IS retroactive.
              hidingDba={dbaHideOnPdf !== (invoice.hide_dba ?? false)}
              onSave={(level) => saveDbaCascade(level, dbaDraft)}
              onCancel={() => setDbaEditMode('closed')}
            >
              {establishments.length > 0 && (
                <Select
                  disabled={dbaHideOnPdf !== (invoice.hide_dba ?? false)}
                  value={dbaDraft === null ? 'none' : String(dbaDraft)}
                  onValueChange={(value) =>
                    setDbaDraft(value === 'none' ? null : parseInt(value, 10))
                  }
                >
                  <SelectTrigger data-testid="bill-to-dba-select">
                    <SelectValue placeholder="Select DBA" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (use client name)</SelectItem>
                    {establishments.map((est) => (
                      <SelectItem key={est.id} value={String(est.id)}>
                        {est.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <button
                type="button"
                className="text-sm text-blue-700 hover:underline inline-flex items-center gap-1"
                data-testid="bill-to-dba-add-new"
                onClick={() => setDbaEditMode('add_dba')}
              >
                <Plus className="w-3.5 h-3.5" />
                add a new DBA for this client
              </button>
              {/* Hide-DBA toggle. The BillToPanel rework moved the shipped DBA
                  editor to this inline picker, which dropped the "Hide DBA on the
                  PDF" control (it survived only in the legacy no-resolution
                  branch that isn't taken live). Christa needs hide-DBA for
                  legal-name-only invoices (e.g. Living Balance / Powell St), and
                  it must be reachable for ANY client — INCLUDING establishment-
                  less ones (hide_dba has no establishment dependency). So it
                  renders here unconditionally, not gated on establishments. */}
              <label
                className="flex items-center gap-1.5 text-sm cursor-pointer"
                data-testid="bill-to-dba-hide-on-pdf-label"
              >
                <input
                  type="checkbox"
                  disabled={dbaDraft !== (invoice.establishment_id ?? null)}
                  checked={dbaHideOnPdf}
                  onChange={(e) => setDbaHideOnPdf(e.target.checked)}
                  data-testid="bill-to-dba-hide-on-pdf"
                />
                <span>Hide DBA on the PDF</span>
              </label>
              <p className="text-xs text-slate-600" data-testid="dba-visibility-separate-action">
                DBA selection and PDF visibility are separate saved choices. Save one before changing the other.
              </p>
            </InlineCascadePicker>
          ) : isEditable && editingField === 'dba' ? (
            /* Legacy hide-on-PDF inline editor (decoupled control retained). */
            <div className="space-y-2" data-testid="bill-to-edit-dba">
              {establishments.length > 0 && (
                <Select
                  disabled={dbaHideOnPdf !== (invoice.hide_dba ?? false)}
                  value={dbaDraft === null ? 'none' : String(dbaDraft)}
                  onValueChange={(value) => {
                    if (value === 'none') {
                      setDbaDraft(null);
                    } else {
                      setDbaDraft(parseInt(value, 10));
                    }
                  }}
                >
                  <SelectTrigger data-testid="bill-to-dba-select">
                    <SelectValue placeholder="Select DBA" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (use client name)</SelectItem>
                    {establishments.map((est) => (
                      <SelectItem key={est.id} value={String(est.id)}>
                        {est.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  disabled={dbaDraft !== (invoice.establishment_id ?? null)}
                  checked={dbaHideOnPdf}
                  onChange={(e) => setDbaHideOnPdf(e.target.checked)}
                  data-testid="bill-to-dba-hide-on-pdf"
                />
                <span>Hide DBA on the PDF</span>
              </label>
              <p className="text-xs text-slate-600">DBA selection and PDF visibility are separate saved choices. Save one before changing the other.</p>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingField(null)}
                  data-testid="bill-to-dba-cancel"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={() => applyDba(dbaDraft, dbaHideOnPdf)}
                  data-testid="bill-to-dba-save"
                >
                  Save
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <p className={VALUE_CLS} data-testid="bill-to-dba-value">
                  {dbaValue}
                </p>
                {/* AC1/AC5/AC9 — resolution-driven source tag. ⚠/amber ONLY for
                    the blocking needs-a-client state; Tier-2 renders confident
                    (✓ …confirm?), NOT a warning. Quiet stored-default tags are
                    suppressed in the dominant case (show=false). */}
                {dbaSourceTag.show && dbaSourceTag.text ? (
                  <p
                    className={`${SOURCE_CLS} ${
                      dbaSourceTag.blocking
                        ? 'text-amber-700 font-medium'
                        : dbaSourceTag.confirm
                          ? 'text-emerald-700'
                          : ''
                    }`}
                    data-testid="bill-to-dba-source"
                    data-tone={dbaSourceTag.tone}
                  >
                    {dbaSourceTag.text}
                    {invoice.hide_dba && (
                      <span data-testid="bill-to-dba-hidden-on-pdf-note">
                        {' · hidden on PDF'}
                      </span>
                    )}
                  </p>
                ) : (
                  showDbaSource && (
                    <p className={SOURCE_CLS} data-testid="bill-to-dba-source">
                      {dbaSource}
                      {invoice.hide_dba && (
                        <span data-testid="bill-to-dba-hidden-on-pdf-note">
                          {' · hidden on PDF'}
                        </span>
                      )}
                    </p>
                  )
                )}
              </div>
              {/* AC9 — always-visible pencil (no hover-only, no kebab). Opens
                  the inline picker when resolution is present, else the legacy
                  hide-toggle editor. When there's a client but no establishment
                  to pick yet, the picker still offers add-DBA (AC7). */}
              {isEditable &&
                (invoice.establishment_id || dbaPrintsOnPdf || (hasClient && !!resolution)) && (
                  <button
                    type="button"
                    className={PENCIL_BTN_CLS}
                    aria-label="Change DBA for this invoice"
                    data-testid="bill-to-dba-edit"
                    onClick={() => {
                      setDbaDraft(invoice.establishment_id ?? null);
                      setDbaHideOnPdf(invoice.hide_dba ?? false);
                      setSaveResult(null);
                      if (resolution) {
                        pickerOpenedAtRef.current = new Date().toISOString();
                        setDbaEditMode('picker');
                      } else {
                        setEditingField('dba');
                      }
                    }}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
            </div>
          )}

          {/* AC6 — inline client picker for the blocking needs-a-client state.
              Clearable in ≤2 taps in the block itself, never a dead-end. Render
              the affordance row when blocked; the actual client picker reuses
              the CLIENT row's stepper (opened from here), so this is the
              in-block entry point Christa can't miss. */}
          {gate.blocked && (
            <div
              className="mt-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900"
              data-testid="bill-to-dba-needs-client"
            >
              <p className="mb-1 font-medium">{gate.reason}</p>
              <button
                type="button"
                className="text-amber-900 underline"
                data-testid="bill-to-dba-pick-client"
                onClick={() => setOrgChangeOpen(true)}
              >
                Pick the client
              </button>
            </div>
          )}

          {/* AC4/AC8 — evidentiary confirmation OR inline error. Always present
              after a Save → a Save is visibly terminal, no silent no-op. */}
          {saveResult && (
            <div
              className={`mt-2 rounded-md px-3 py-2 text-sm ${
                saveResult.kind === 'success'
                  ? 'bg-emerald-50 border border-emerald-200 text-emerald-900'
                  : 'bg-red-50 border border-red-200 text-red-900'
              }`}
              data-testid={
                saveResult.kind === 'success'
                  ? 'bill-to-dba-save-confirmation'
                  : 'bill-to-dba-save-error'
              }
              role="status"
            >
              {saveResult.message}
            </div>
          )}
        </div>

        {/* ============ BILLING ADDRESS ============ */}
        <div className="py-3" data-testid="bill-to-row-address">
          <FieldLabel>Billing Address</FieldLabel>
          {isEditable && editingField === 'address' ? (
            <div className="space-y-2" data-testid="bill-to-edit-address">
              <div className="flex flex-col gap-1.5 text-sm">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="bill-to-address-source"
                    value="project"
                    checked={addressMode === 'project'}
                    onChange={() => {
                      // B5: seed ref synchronously so a same-tick Save click
                      // doesn't race the state update.
                      addressModeRef.current = 'project';
                      setAddressMode('project');
                    }}
                    data-testid="bill-to-address-source-project"
                  />
                  <span>Project billing address (default)</span>
                </label>
                {invoice.establishment_id && (
                  <label
                    className={`flex items-center gap-1.5 ${
                      addressState?.establishment_address_set
                        ? 'cursor-pointer'
                        : 'cursor-not-allowed opacity-60'
                    }`}
                  >
                    <input
                      type="radio"
                      name="bill-to-address-source"
                      value="establishment"
                      checked={addressMode === 'establishment'}
                      onChange={() => {
                        addressModeRef.current = 'establishment';
                        setAddressMode('establishment');
                      }}
                      disabled={!addressState?.establishment_address_set}
                      data-testid="bill-to-address-source-establishment"
                    />
                    {/* UX1 (v0.14 polish): "DBA" vocab in user-facing labels. */}
                    <span>Use DBA address</span>
                  </label>
                )}
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="bill-to-address-source"
                    value="organization"
                    checked={addressMode === 'organization'}
                    onChange={() => {
                      addressModeRef.current = 'organization';
                      setAddressMode('organization');
                    }}
                    data-testid="bill-to-address-source-organization"
                  />
                  <span>Use client address</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="bill-to-address-source"
                    value="custom"
                    checked={addressMode === 'custom'}
                    onChange={() => {
                      addressModeRef.current = 'custom';
                      setAddressMode('custom');
                    }}
                    data-testid="bill-to-address-source-custom"
                  />
                  <span>Custom address for this invoice</span>
                </label>
              </div>
              {addressMode === 'custom' && (
                <textarea
                  value={addressDraft}
                  onChange={(e) => {
                    // B5: mirror draft into the ref synchronously so the
                    // very next Save click reads the latest characters even
                    // if React hasn't flushed the state update yet.
                    addressDraftRef.current = e.target.value;
                    setAddressDraft(e.target.value);
                  }}
                  placeholder="Enter custom billing address"
                  className="w-full min-h-[72px] p-2 border rounded resize-y text-sm"
                  data-testid="bill-to-address-textarea"
                  autoFocus
                />
              )}
              <div className="flex gap-2 justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingField(null)}
                  data-testid="bill-to-address-cancel"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    // Polish wave 3 — W3-3 (2026-05-30): synchronous
                    // re-entry gate. Triple-clicking Save used to fire
                    // applyAddress 3× before the propagation modal
                    // opened (custom path) or the inline mutate landed
                    // (non-custom path). Same pattern as W3-2 on
                    // PropagationModal + B2 on OrgChangeStepper.
                    if (addressSaveInFlightRef.current) return;
                    addressSaveInFlightRef.current = true;
                    // Polish wave 2 — B5 (2026-05-30): read from refs to
                    // guarantee we apply the LATEST mode + draft regardless
                    // of any potential stale-closure race. See ref declaration
                    // above for the full investigation note.
                    applyAddress(
                      addressModeRef.current,
                      addressDraftRef.current,
                    );
                  }}
                  data-testid="bill-to-address-save"
                >
                  Save
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <p
                  className={`${VALUE_CLS} whitespace-pre-line`}
                  data-testid="bill-to-address-value"
                >
                  {addressValue || (
                    <span className="text-amber-700 italic text-sm font-normal">
                      No address set
                    </span>
                  )}
                </p>
                {showAddressSource && (
                  <p className={`${SOURCE_CLS} flex items-center`}>
                    {/* Polish wave 2 — B3 (2026-05-30): slate chip surface
                        matches OrgChangeConfirm's address-source chip so the
                        signal is consistent across BILL TO. */}
                    <span
                      className={ADDRESS_SOURCE_CHIP_CLS}
                      data-testid="bill-to-address-source"
                      data-change-kind="unchanged"
                    >
                      {addressSourceLabel}
                    </span>
                  </p>
                )}
              </div>
              {isEditable && (
                <button
                  type="button"
                  className={PENCIL_BTN_CLS}
                  aria-label="Edit billing address"
                  data-testid="bill-to-address-edit"
                  onClick={() => {
                    const src = (invoice.frozen_billing_address !== null && invoice.frozen_billing_address !== undefined
                      ? 'custom' : invoice.billing_address_source ?? 'project') as
                      | 'project'
                      | 'establishment'
                      | 'organization'
                      | 'custom';
                    const draft = invoice.frozen_billing_address ?? invoice.billing_address_override ?? '';
                    setAddressMode(src);
                    setAddressDraft(draft);
                    // B5: also seed refs synchronously so any same-tick
                    // Save click on the popped editor reads the just-opened
                    // values rather than the previous editor's stale state.
                    addressModeRef.current = src;
                    addressDraftRef.current = draft;
                    // W3-3: reset the Save race-guard whenever the editor
                    // opens. Closing-without-save via Cancel or pencil
                    // re-click shouldn't leave the guard perma-locked.
                    addressSaveInFlightRef.current = false;
                    setEditingField('address');
                  }}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
        </div>

        {fieldProvenance && (['legal_name', 'display_name', 'dba', 'billing_address'] as InvoiceIdentityFieldName[]).some((field) => fieldProvenance[field] && fieldProvenance[field]?.kind !== 'inherited') && (
          <p className="py-3 text-sm text-slate-700" data-testid="bill-to-source-exception">
            {(['legal_name', 'display_name', 'dba', 'billing_address'] as InvoiceIdentityFieldName[])
              .filter((field) => fieldProvenance[field] && fieldProvenance[field]?.kind !== 'inherited')
              .map((field) => {
                const label = { legal_name: 'Legal name', display_name: 'Client display name', dba: 'Trading name', billing_address: 'Billing address' }[field];
                return `${label} ${fieldProvenance[field]?.kind === 'kept_during_edit' ? 'kept from an earlier edit' : 'set just for this invoice'}`;
              }).join('. ')}.
          </p>
        )}

        {fieldProvenance && <details className="py-3" data-testid="bill-to-field-provenance">
          <summary className="cursor-pointer text-sm text-slate-600 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600">Where these details come from</summary>
          <p className="mt-2 text-xs text-slate-600">Future defaults for new invoices stay unchanged when you use a current source value here.</p>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {(['legal_name', 'display_name', 'dba', 'billing_address'] as InvoiceIdentityFieldName[]).map((field) => {
              const state = fieldProvenance[field];
              const label = { legal_name: 'Legal name', display_name: 'Client display name',
                dba: 'DBA', billing_address: 'Billing address' }[field];
              return <div key={field} className="rounded border border-slate-100 p-2 text-sm">
                <span>{label}: {!state ? 'Source details unavailable' : state.kind === 'kept_during_edit' ? 'kept during an earlier edit'
                  : state?.kind === 'explicit_override' ? 'invoice-specific choice' : 'following current source'}</span>
                {state && state.kind !== 'inherited' && <button type="button"
                  className="ml-2 text-blue-700 underline"
                  data-testid={`follow-default-${field}`}
                  onClick={() => setReviewIntent({ intent: 'follow_default', invoice_id: invoice.id,
                    follow_fields: [field] })}>
                  {field === 'billing_address'
                    ? `Use ${currentSourceAddress.label}: ${currentSourceAddress.value || '(blank)'}`
                    : 'Review exact current source value'}
                </button>}
              </div>;
            })}
          </div>
        </details>}

        {/* ============ RECIPIENTS ============ */}
        <div className="py-3" data-testid="bill-to-row-recipients">
          <FieldLabel>Recipients</FieldLabel>

          {/* bill-to fix 2026-05: keep the empty-TO fallback warning. */}
          {toContactsMeta &&
            toContactsMeta.explicit_count === 0 &&
            toContactsMeta.fallback_active &&
            toContactsMeta.fallback_contact &&
            !toContactsMeta.suppress_attention_line && (
              <div
                className="mb-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900"
                data-testid="bill-to-fallback-warning"
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-700" />
                  <p>
                    No TO recipient set. Email will fall back to{' '}
                    <span className="font-semibold">
                      {toContactsMeta.fallback_contact.contact_name}
                    </span>{' '}
                    (client Primary).
                  </p>
                </div>
              </div>
            )}
          {toContactsMeta &&
            toContactsMeta.explicit_count === 0 &&
            toContactsMeta.suppress_attention_line && (
              <div
                className="mb-2 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-700"
                data-testid="bill-to-suppressed-note"
              >
                <div className="flex items-start gap-2">
                  <MailX className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-500" />
                  {/* NOT "for this invoice". Under V211 this flag is almost
                      always set by the system at CREATE, inherited from a
                      project-level clear -- so calling a project decision
                      per-invoice understates its durability, which is the
                      vocabulary she has pushed back on three times and the
                      shape of "it said it would only apply to that invoice"
                      (2026-07-15, inv 635). Under-promising sends her back to
                      re-do a setting that already holds. */}
                  <span>
                    {projectPin?.cleared
                      ? `No recipients are saved for ${invoice.project_name || 'this project'}, so the client's Primary contact won't be added.`
                      : "The client's Primary contact won't be added to this invoice."}
                  </span>
                </div>
              </div>
            )}

          {/* UX4 (v0.14 polish): when the invoice has ZERO recipients AND
              the fallback-warning above isn't already amber-flagging the
              same case (no toContactsMeta passed, or fallback inactive),
              render the V175-style amber warning instead of the muted
              "No recipients on this invoice yet" caption. Per §0.0:
              87% of Banks invoices have zero recipients — this is the
              dominant path, not an edge case. Christa needs to see this
              shouting before she clicks Send, otherwise the PDF silently
              falls back to email org Primary. */}
          {recipientsMeta.length > 0 ? (
            <>
            <ul className="space-y-1.5" data-testid="bill-to-recipients-list">
              {recipientsMeta.map((row) => {
                const dangling =
                  row.source === 'user_added' && !row.name && !row.email;
                const isStarFilled = row.is_dba_accounting || row.is_org_accounting;
                const canStar = !!row.contact_id && !dangling;
                const rowKey =
                  row.invoice_contact_id ||
                  `${row.contact_id}-${row.email}-${row.role}`;
                return (
                  <li
                    key={rowKey}
                    className="flex items-center gap-2"
                    data-testid={`bill-to-recipient-row-${row.invoice_contact_id ?? 'unknown'}`}
                  >
                    <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                      {dangling ? (
                        <span className="text-sm text-slate-500 italic">
                          (removed contact)
                        </span>
                      ) : (
                        <>
                          {canChangeRole && row.invoice_contact_id ? (
                            <RecipientRoleSelect
                              personName={row.name || row.email || 'this person'}
                              role={row.role}
                              onChoose={(next) => chooseRole(row, next)}
                            />
                          ) : (
                            <RoleBadge role={row.role} />
                          )}
                          <span className="text-sm font-medium text-slate-900 truncate">
                            {row.name || '(no name)'}
                          </span>
                          {row.email && (
                            <span className="text-sm text-slate-600 truncate">
                              {row.email}
                            </span>
                          )}
                          {row.source === 'typed_in' && (
                            // Christa-friction-polish CRIT-4 / 2b: demoted
                            // from amber chip to slate caption text. Same
                            // reasoning as 2a: typed-in is a status, not a
                            // warning — keep amber for real warnings (empty
                            // recipients fallback). Reads as the natural
                            // tail of the recipient row.
                            <span
                              className="text-xs text-slate-500"
                              title="One-off recipient (only on this invoice)"
                              data-testid={`bill-to-recipient-oneoff-${row.invoice_contact_id ?? 'unknown'}`}
                            >
                              · one-off
                            </span>
                          )}
                          {/* R3 (SPEC-invoice-send-safety-2026-08-17): the
                              affordance for her complaint — "it still looks
                              like the accounting person shows as a one off."
                              She should never have to know a separate save
                              flow exists; the button sits right where the
                              one-off label already is. Promotes IN PLACE
                              (same invoice_contacts row, contact_id repointed),
                              never removes and re-adds. Hidden once a contact
                              backs the row (AC10) and on locked invoices. */}
                          {canSaveForProject(row) && (
                            <button
                              type="button"
                              className="text-xs text-blue-700 hover:underline flex-shrink-0 disabled:opacity-50 disabled:no-underline"
                              aria-label={`Save ${row.name || row.email} for ${projectLabelForRoles} going forward`}
                              data-testid={`bill-to-recipient-promote-project-${row.invoice_contact_id}`}
                              disabled={promoteContact.isPending}
                              onClick={() => applyPromoteToProject(row)}
                            >
                              Save for this project
                            </button>
                          )}
                          {row.source === 'typed_in' && isEditable && row.invoice_contact_id && (
                            <button
                              type="button"
                              className="text-xs text-blue-700 hover:underline flex-shrink-0 disabled:opacity-50 disabled:no-underline"
                              data-testid={`bill-to-recipient-promote-${row.invoice_contact_id}`}
                              disabled={promoteContact.isPending}
                              onClick={() =>
                                promoteContact.mutate({
                                  invoiceId: invoice.id,
                                  rowId: row.invoice_contact_id!,
                                })
                              }
                            >
                              Save to client
                            </button>
                          )}
                        </>
                      )}
                    </div>
                    {/* Hotfix2 — Bug #3: hide star + remove on locked invoices */}
                    {isEditable && canStar && (
                      <button
                        type="button"
                        className="text-slate-400 hover:text-amber-600 transition-colors flex-shrink-0 p-1 rounded"
                        aria-label={
                          isStarFilled
                            ? `Remove ${row.name} as default accounting contact`
                            : `Save ${row.name} as default accounting contact`
                        }
                        aria-pressed={isStarFilled}
                        data-testid={`bill-to-recipient-star-${row.invoice_contact_id ?? 'unknown'}`}
                        onClick={() => applyStarToggle(row)}
                      >
                        <Star
                          className="w-4 h-4"
                          fill={isStarFilled ? 'currentColor' : 'none'}
                          color={isStarFilled ? '#f59e0b' : undefined}
                        />
                      </button>
                    )}
                    {isEditable && row.invoice_contact_id && (
                      <button
                        type="button"
                        className="text-slate-400 hover:text-red-600 transition-colors flex-shrink-0 p-1 rounded"
                        aria-label={`Remove ${row.name ?? 'recipient'}`}
                        data-testid={`bill-to-recipient-remove-${row.invoice_contact_id}`}
                        onClick={() => applyRemoveRecipient(row)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
            {roleRefusal && (
              <p className="mt-1.5 text-sm text-amber-800" role="alert" data-testid="role-refusal">{roleRefusal}</p>
            )}
            </>
          ) : (
            // UX4 (v0.14 polish): amber warning replaces muted slate copy when
            // the invoice has zero recipients AND the existing fallback-warning
            // block (above) isn't already showing one. Two suppression paths:
            //   1. toContactsMeta.fallback_active === true with a fallback contact
            //      → the warning above already explains the silent EMAIL fallback.
            //   2. toContactsMeta.suppress_attention_line === true
            //      → the user has explicitly suppressed primary-contact fallback;
            //      the muted note above tells them so, and we don't want a
            //      shouting amber here as well.
            (() => {
              const fallbackShown =
                !!toContactsMeta &&
                toContactsMeta.explicit_count === 0 &&
                toContactsMeta.fallback_active &&
                !!toContactsMeta.fallback_contact &&
                !toContactsMeta.suppress_attention_line;
              const suppressShown =
                !!toContactsMeta &&
                toContactsMeta.explicit_count === 0 &&
                toContactsMeta.suppress_attention_line;
              if (fallbackShown || suppressShown) {
                // Higher-signal warning already rendered above; just a quiet
                // line here is fine.
                return (
                  <p className="text-sm text-slate-500" data-testid="bill-to-no-recipients">
                    No recipients on this invoice yet.
                  </p>
                );
              }
              return (
                <div
                  className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900"
                  data-testid="bill-to-no-recipients-warning"
                >
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-700" />
                    <p>
                      <span className="font-semibold">Send without TO recipient.</span>{' '}
                      Email will silently fall back to the client&apos;s Primary
                      contact when the invoice goes out. Add a recipient below
                      to control who receives it.
                    </p>
                  </div>
                </div>
              );
            })()
          )}

          {/* Project-pin visibility (2026-08-08, rewritten same day after a
              Christa review found the first version wrong three ways).

              A saved project pin used to say NOTHING on the invoice, which is
              most of why she kept re-doing the same correction: there was no way
              to tell a pin that worked from one that never took.

              What this must NOT do is claim more than it knows. The first
              version keyed on `resolution.recipients.source === 'project_default'`
              and on `recipientsMeta.length`, and both were the wrong object:

                * that source is ALSO returned for an establishment-scoped
                  accounting set, which is DBA-wide -- it applies to every
                  project billing under that DBA. "Saved for this project" would
                  have been a lie about blast radius, on the exact axis she
                  polices (2026-05-28, inv 567/447: "it should only be the same
                  legal entity in this particular project").
                * recipientsMeta is THIS INVOICE's contacts. Remove someone from
                  one invoice -- the pre-selected default -- and the list empties
                  while the pin stays full, so the empty copy would have said
                  "new invoices start empty" with the removed person still
                  pinned. That is inv 609 ("thought it stuck but guess not!!")
                  with the screen now agreeing in writing.

              So it reads the pin itself, which the backend now reports as
              `project_recipient_pin: {count, cleared}`. */}
          {projectPin && projectPin.count > 0 && (
            <p
              className="mt-2 text-xs text-slate-500"
              data-testid="bill-to-recipients-project-pin-note"
            >
              {/* NAMES the pinned people rather than pointing at the list above.
                  The invoice's own recipients are frequently not the pin — she
                  removes one for a single invoice, or opens an older sent
                  invoice carrying that invoice's snapshot — so "these contacts"
                  would have pointed at the wrong rows, or at none at all. */}
              New invoices on this project go to{' '}
              <span className="font-medium">{formatPinnedNames(projectPin)}</span>.
              {' '}Other
              {invoice.client_name ? ` ${invoice.client_name}` : ' client'}{' '}
              contacts won&apos;t be added automatically.
            </p>
          )}
          {projectPin && projectPin.count === 0 && projectPin.cleared && (
            <p
              className="mt-2 text-xs text-slate-500"
              data-testid="bill-to-recipients-project-pin-cleared-note"
            >
              {/* Says "no SAVED recipients", not "starts empty". An invoice with
                  no recipients still addresses the PDF Attention block and
                  pre-fills the send box with the client's Primary contact, and
                  the amber warning directly above says so. Two adjacent messages
                  disagreeing about who gets it is worse than no message.

                  Which is also why the fallback clause is CONDITIONAL: when the
                  operator has suppressed the Attention fallback, or the client
                  has no Primary contact at all, there is no fallback to promise
                  and asserting one would be the same contradiction pointed the
                  other way. */}
              No recipients are saved for this project, so new invoices start
              with none
              {primaryFallbackActive
                ? <>; the PDF and email still fall back to{' '}
                    {toContactsMeta?.fallback_contact?.contact_name
                      || `${invoice.client_name || 'the client'}'s Primary contact`}.</>
                : '.'}
            </p>
          )}

          {/* Add recipient — popover with pool picker + typed-in form */}
          {/* Hotfix2 — Bug #3: hide on locked invoices (sent/paid/etc). */}
          {isEditable && (
          <div className="mt-2">
            <Popover
              open={editingField === 'recipient_add'}
              onOpenChange={(open) => {
                setEditingField(open ? 'recipient_add' : null);
                if (!open) {
                  setOneOffName('');
                  setOneOffEmail('');
                }
              }}
            >
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="text-sm text-blue-700 hover:underline inline-flex items-center gap-1"
                  data-testid="bill-to-add-recipient"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add recipient
                </button>
              </PopoverTrigger>
              <PopoverContent
                className="w-80 p-3"
                data-testid="bill-to-add-recipient-popover"
              >
                {/* Pool picker: DBA then client-level contacts, then everyone else. A person who is
                    already on this invoice stays visible but cannot be added again (R1 AC19). */}
                {eligible && (
                  <div className="space-y-3 mb-3">
                    {(eligible.establishment_contacts?.length ?? 0) > 0 &&
                      renderPoolGroup(
                        // UX1 (v0.14 polish): "DBA" vocab in heading copy.
                        `From ${invoice.establishment_name || 'DBA'}`,
                        eligible.establishment_contacts,
                        (c) => c.emails?.[0] ?? '',
                      )}
                    {(eligible.org_contacts?.length ?? 0) > 0 &&
                      renderPoolGroup('From client', eligible.org_contacts, (c) => c.emails?.[0] ?? '')}
                    {/* R1 (hotfix v0.14.5): general (non-accounting) contact pool, previously invisible.
                        `email` scalar fallback for general rows that predate the emails[] column. */}
                    {(eligible.general_contacts?.length ?? 0) > 0 &&
                      renderPoolGroup(
                        'Other contacts',
                        eligible.general_contacts ?? [],
                        (c) => c.emails?.[0] ?? c.email ?? '',
                      )}
                  </div>
                )}
                {/* Typed-in form */}
                <div className="border-t border-slate-100 pt-3 space-y-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    Or type a new recipient
                  </p>
                  <Input
                    value={oneOffName}
                    onChange={(e) => setOneOffName(e.target.value)}
                    placeholder="Name"
                    data-testid="bill-to-add-recipient-name"
                  />
                  <Input
                    value={oneOffEmail}
                    onChange={(e) => setOneOffEmail(e.target.value)}
                    placeholder="email@example.com"
                    type="email"
                    data-testid="bill-to-add-recipient-email"
                  />
                  {typedEmailIsOnInvoice && (
                    <p className="text-xs text-red-700" role="alert" data-testid="bill-to-add-recipient-duplicate">
                      {oneOffEmail.trim()} is already on this invoice.
                    </p>
                  )}
                  <Button
                    size="sm"
                    className="w-full"
                    disabled={!oneOffName.trim() || !oneOffEmail.trim() || typedEmailIsOnInvoice}
                    data-testid="bill-to-add-recipient-submit"
                    onClick={() =>
                      applyAddRecipient({
                        kind: 'typed_in',
                        name: oneOffName.trim(),
                        email: oneOffEmail.trim(),
                        // role omitted → backend auto-assigns (AC8)
                      })
                    }
                  >
                    {/* Was "Add as one-off" — the literal thing she complained
                        about ("it only allows to add another person as a 'one
                        off'"). It is now a choice made in the next step, so the
                        button must not pre-announce the answer. */}
                    Add recipient
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          )}
        </div>

        {/* ============ PROJECT (read-only + edit-in-project signpost) ============ */}
        {/* R3 (invoice-identity-correctable) — G2 / AC3: the project name (and
            any project-sourced field) is edited in the PROJECT view, not on the
            invoice — the invoice renders it LIVE. Show an inline "edit in
            project" signpost right here in the identity panel so Christa never
            has to know which screen owns the field (the 602 "Art of the Line"
            project-name typo crossed a screen boundary with no signpost — G2). */}
        <div className="py-3" data-testid="bill-to-row-project">
          <FieldLabel>Project</FieldLabel>
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <p className={VALUE_CLS} data-testid="bill-to-project-name">
                {invoice.project_name || (
                  <span className="text-slate-400 italic">No project</span>
                )}
              </p>
              <p className={SOURCE_CLS} data-testid="bill-to-project-source">
                The project name and address are set in the project.
              </p>
            </div>
            {invoice.project_id && (
              <Link
                to={`/projects/${invoice.project_id}?section=invoices`}
                className="text-sm text-blue-700 hover:underline inline-flex items-center gap-1 flex-shrink-0"
                data-testid="bill-to-project-edit"
                title="Edit this in the project"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Edit in project
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Propagation modal — single instance, parameterized.
          Hotfix2 — Bug #3: gated on isEditable as belt-and-braces. The
          pencils/buttons are already hidden on locked invoices, but in case
          a stale `pending` setter ever fires (programmatic ref / future
          imperative code path), refuse to open the modal too. */}
      <RoleChangeDialog
        request={roleChange}
        busy={updateContact.isPending}
        onCancel={() => setRoleChange(null)}
        onConfirm={(choice) => {
          if (!roleChange) return;
          const { rowId, newRole } = roleChange;
          updateContact.mutate(
            { invoiceId: invoice.id, rowId,
              data: choice === 'project' ? { role: newRole, also_project_default: true } : { role: newRole } },
            { onSuccess: () => setRoleChange(null) },
          );
        }}
      />
      <PropagationModal
        open={isEditable && pending !== null}
        onClose={closePending}
        onConfirm={async (mode, extras) => {
          if (pending) await pending.apply(mode, extras);
        }}
        field={pending?.field ?? 'establishment'}
        availableModes={pending?.availableModes ?? ['invoice_only']}
        defaultMode={pending?.defaultMode ?? 'invoice_only'}
        context={pending?.context ?? {}}
        oldValue={pending?.oldValue}
        newValue={pending?.newValue}
        starToggleAction={pending?.starToggleAction}
        starProjectLabel={pending?.starProjectLabel}
        starProjectSaveState={pending?.starProjectSaveState}
        modeNotes={pending?.modeNotes}
        recipientAdd={pending?.recipientAdd}
      />

      {/* Wave 3 (v0.14 org-change) stepper. Gated on isEditable as
          belt-and-braces — pencil is already disabled when invoice is
          locked, but if a stale ref ever flips orgChangeOpen, refuse to
          render the stepper at all. */}
      {reviewIntent && <InvoiceChangeReview
        intent={reviewIntent}
        focusedInvoiceId={invoice.id}
        focusedProjectIds={reviewProjectIds.length ? reviewProjectIds : invoice.project_id ? [invoice.project_id] : []}
        onClose={() => setReviewIntent(null)}
        onApplied={(result) => {
          queryClient.invalidateQueries({ queryKey: ['invoices', invoice.id, 'full'] });
          queryClient.invalidateQueries({ queryKey: ['invoices', invoice.id] });
          queryClient.invalidateQueries({ queryKey: ['invoices', invoice.id, 'document-history'] });
          const invoiceCount = result.changed_invoice_ids.length;
          const futureDefaultCount = result.selected_project_default_scope_ids.length
            + result.selected_project_ids.length
            + (reviewIntent.intent === 'identity_default' && reviewIntent.default_level === 'this_client' ? 1 : 0);
          setSaveResult({ kind: 'success',
            message: `Reviewed change saved for ${invoiceCount} ${invoiceCount === 1 ? 'invoice' : 'invoices'} and ${futureDefaultCount} future ${futureDefaultCount === 1 ? 'default' : 'defaults'}.`,
            affectedNumbers: [] });
          setReviewIntent(null);
          setReviewProjectIds([]);
        }}
      />}
      {invoice.project_id && invoice.organization_id && <Dialog open={projectPayerOpen} onOpenChange={setProjectPayerOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <ProjectPayerChoice
            projectId={invoice.project_id}
            organizationId={invoice.organization_id}
            onCancel={() => setProjectPayerOpen(false)}
            onIntent={(intent, focusedProjectIds) => {
              setReviewIntent(intent);
              setReviewProjectIds(focusedProjectIds);
              setProjectPayerOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>}
      <OrgChangeStepper
        open={isEditable && orgChangeOpen}
        onClose={() => setOrgChangeOpen(false)}
        onProjectPayerFlow={() => {
          setOrgChangeOpen(false);
          setProjectPayerOpen(true);
        }}
        invoice={{
          id: invoice.id,
          organization_id: invoice.organization_id,
          client_name: invoice.client_name,
          project_id: invoice.project_id,
          project_name: invoice.project_name,
          establishment_id: invoice.establishment_id,
          establishment_name: invoice.establishment_name,
          billing_address_source: invoice.billing_address_source,
          billing_address_override: invoice.billing_address_override,
          org_billing_address: invoice.org_billing_address,
          org_address: invoice.org_address,
          project_address: invoice.project_address,
          establishment_address: invoice.establishment_address,
        }}
        recipientsToBeCleared={orgChangeRecipientsPreview}
        // §5.13 carry-through is now handled atomically inside the cascade
        // PUT (R8e/R8f fixwave 2026-05-28): the stepper includes
        // `establishment_id` in the PUT body and the backend sets it in
        // the single cascade transaction. No follow-up mutation needed
        // on the BillToPanel side.
      />
    </section>
  );
}
